import { registrarEvento } from "../core/auditLog.js";
import { C, CARGO_LABELS, CUENTAS_RETENCION, DOC_INFO, EMPRESA, ESTADOS } from "../core/config.js";
import { ivaPct } from "../core/cloudSync.js";
import { desgloseIVA, itemsSinIVA, regimenDeLinea, renglonesIVA } from "../core/iva.js";
import { descripcionKitCatalogo, esProductoKit, productoPorNombre } from "../core/kitCatalogo.js";
import { contactoImprimible } from "../core/contactos.js";
import { clienteDeDoc } from "../core/terceros.js";
import { htmlParaMostrar } from "../core/textoRico.js";
import { getFormatoDoc } from "../core/formatoDoc.js";
import { textoTiempoEntrega } from "../core/tiempoEntrega.js";
import { state } from "../core/state.js";
import { embebidasParaDocumento } from "../core/imagenProducto.js";
import { addDias, escapeHTML, fmtCOP, fmtDate, montoEnLetras, parseNumber, today } from "../core/utils.js";
import { getRetencionesDetalle } from "../modules/contabilidad.js";
import { abrirDocumentoEnApp } from "../ui/visorDoc.js";
import { calcularRetencionesVenta } from "../core/accounting.js";

export function exportCertificadoRetencion(terceroId, desde, hasta) {
  const prov = (state.data.proveedores||[]).find(p => p.id === terceroId) || {};
  const detalle = getRetencionesDetalle(desde, hasta).filter(d => d.tercero_id === terceroId);

  const filasHTML = detalle.map(d => {
    const cfg = CUENTAS_RETENCION[d.cuenta];
    const numOrigen = d.origen ? d.origen.numero : "—";
    return `<tr><td>${fmtDate(d.fecha)}</td><td>${escapeHTML(numOrigen)}</td><td>${cfg.corto}</td><td style="text-align:right;">${fmtCOP(d.base)}</td><td style="text-align:right;">${d.tarifa.toFixed(2)}%</td><td style="text-align:right;font-weight:700;">${fmtCOP(d.valor)}</td></tr>`;
  }).join("");

  const totalesPorTipo = {};
  detalle.forEach(d => { totalesPorTipo[d.cuenta] = (totalesPorTipo[d.cuenta]||0) + d.valor; });
  const totalGeneral = detalle.reduce((s,d)=>s+d.valor,0);

  const resumenHTML = Object.entries(CUENTAS_RETENCION).map(([cuenta,cfg]) => {
    const v = totalesPorTipo[cuenta] || 0;
    return v > 0 ? `<tr><td>${cfg.label}</td><td style="text-align:right;font-weight:700;">${fmtCOP(v)}</td></tr>` : "";
  }).join("");

  const estilos = `
    body{font-family:Arial,sans-serif;color:#1e293b;padding:30px;font-size:13px;}
    h1{font-size:18px;margin:0 0 4px;}
    table{width:100%;border-collapse:collapse;margin-top:14px;font-size:12px;}
    th,td{border:1px solid #cbd5e1;padding:6px 8px;}
    th{background:#f1f5f9;text-align:left;}
    .header{display:flex;justify-content:space-between;align-items:center;border-bottom:3px solid #0E7490;padding-bottom:10px;margin-bottom:14px;}
    .totales{margin-top:14px;max-width:380px;}
    @page{margin:15mm;size:A4}
    @media print{body{padding:0}.no-print{display:none}}
    html.__pdf body{padding:0}
    html.__pdf .no-print{display:none}
  `;
  const cuerpo = `
  <div class="header">
    <div>
      <h1>CERTIFICADO DE RETENCIÓN</h1>
      <div>${escapeHTML(EMPRESA.nombre)} - NIT ${escapeHTML(EMPRESA.nit)}</div>
    </div>
    <div style="text-align:right;">
      <div><strong>Período:</strong> ${fmtDate(desde)} a ${fmtDate(hasta)}</div>
      <div><strong>Fecha de expedición:</strong> ${fmtDate(today())}</div>
    </div>
  </div>
  <p><strong>Agente retenedor:</strong> ${escapeHTML(EMPRESA.nombre)} (NIT ${escapeHTML(EMPRESA.nit)}-${escapeHTML(EMPRESA.dv||"")})</p>
  <p><strong>Beneficiario / Sujeto de retención:</strong> ${escapeHTML(prov.nombre||"—")} — ${escapeHTML(prov.tipo_documento||"NIT")} ${escapeHTML(prov.nit||"—")}${prov.dv?"-"+escapeHTML(prov.dv):""}</p>

  <h3>Detalle de retenciones practicadas</h3>
  <table>
    <thead><tr><th>Fecha</th><th>Documento (OCP)</th><th>Concepto</th><th style="text-align:right;">Base</th><th style="text-align:right;">Tarifa</th><th style="text-align:right;">Valor retenido</th></tr></thead>
    <tbody>${filasHTML || `<tr><td colspan="6" style="text-align:center;color:#64748b;">Sin retenciones en el período</td></tr>`}</tbody>
  </table>

  <h3>Resumen por concepto</h3>
  <table class="totales">
    <thead><tr><th>Concepto</th><th style="text-align:right;">Valor total retenido</th></tr></thead>
    <tbody>${resumenHTML || `<tr><td colspan="2" style="text-align:center;color:#64748b;">Sin retenciones</td></tr>`}</tbody>
    <tfoot><tr style="font-weight:800;background:#f1f5f9;"><td>TOTAL RETENIDO</td><td style="text-align:right;">${fmtCOP(totalGeneral)}</td></tr></tfoot>
  </table>

  <p style="margin-top:30px;font-size:11px;color:#64748b;">Este certificado se expide con base en los registros contables del sistema. Su validez para efectos de declaración de renta debe ser confirmada por el área contable/fiscal de la empresa.</p>
  <p style="margin-top:40px;">_____________________________<br>${escapeHTML(EMPRESA.nombre)}<br>NIT ${escapeHTML(EMPRESA.nit)}</p>
  `;

  // Antes esta era la única salida que se imprimía SOLA, sin dejar ver el
  // documento primero. Ahora se comporta como los demás: se ve y se descarga.
  abrirVentanaDoc(`Certificado de retención - ${prov.nombre || ""}`.trim(), cuerpo,
    { estilos, margenMM: 15 });
}

// ============================================================================
// Formatos impresos de documentos (CT, OP, OCP, RC)
// Réplica de los formatos corporativos en Excel, con la identidad de la empresa:
// azul institucional (C.mathiz) + dorado (C.gold) en líneas y encabezados.
// ============================================================================

const DOC_VERSION = { CT: "02", OP: "01", OCP: "01", RC: "01" };
const NAVY = C.mathiz;
const GOLD = C.gold;
const GRIS_LINEA = "#d5dbea";

// Estilos compartidos por los cuatro formatos
function estilosDoc() {
  return `
    *{margin:0;padding:0;box-sizing:border-box;-webkit-print-color-adjust:exact;print-color-adjust:exact;}
    body{font-family:Arial,Helvetica,sans-serif;font-size:12px;color:#1f2937;padding:28px;position:relative;line-height:1.45;}
    .hdr{display:flex;justify-content:space-between;align-items:center;gap:14px;padding-bottom:10px;}
    .hdr-logo{height:64px;}
    /* Firma vertical del fabricante, en el borde derecho y centrada en la hoja.
       position:fixed para que se repita en todas las páginas al imprimir, igual
       que la marca de agua de borrador. */
    .marca-lateral{
      /* De abajo hacia arriba: vertical-rl escribe de arriba abajo, así que se
         gira 180°. La rotación es sobre el centro del propio elemento, de modo
         que no altera el centrado vertical que hace el translateY. (sideways-lr
         haría esto mismo sin girar, pero no se puede dar por soportado en todos
         los navegadores desde los que se imprime.) */
      position:fixed;right:3.5mm;top:50%;transform:translateY(-50%) rotate(180deg);
      writing-mode:vertical-rl;text-orientation:mixed;
      font-size:8px;font-weight:700;letter-spacing:2px;color:#aab1c2;
      white-space:nowrap;pointer-events:none;z-index:5;
    }
    .marca-lateral .ia{color:${GOLD};font-weight:800;}
    .goldline{height:2.5px;background:linear-gradient(90deg,${NAVY} 0%,${GOLD} 100%);margin:0 0 20px;border-radius:2px;}
    .lbl{font-size:9px;font-weight:700;color:${NAVY};letter-spacing:.6px;text-transform:uppercase;}
    .val{font-size:11px;color:#1f2937;}
    .box{background:#f7f9fd;border-radius:10px;}
    .boxtitle{color:${NAVY};font-size:10px;font-weight:800;letter-spacing:1.2px;padding:12px 16px 0;}
    .boxtitle:after{content:"";display:block;height:2px;width:48px;background:linear-gradient(90deg,${NAVY},${GOLD});margin-top:5px;border-radius:2px;}
    .boxbody{padding:9px 16px 13px;}
    .frow{display:flex;gap:8px;margin-bottom:5px;font-size:11px;}
    .frow .k{font-weight:700;color:#6d7896;font-size:9px;min-width:104px;text-transform:uppercase;letter-spacing:.4px;padding-top:2px;}
    /* Va en el centro del encabezado, llenando el hueco que antes quedaba vacío
       entre los datos de la empresa y el bloque del documento. */
    .quality{border-collapse:collapse;font-size:9px;color:#8a91a3;flex-shrink:0;}
    .quality td{padding:1px 0 1px 12px;text-align:right;}
    .quality td:first-child{font-weight:700;color:#b0b6c6;letter-spacing:.5px;}
    table.items{border-collapse:collapse;width:100%;margin-top:22px;}
    table.items thead tr{background:linear-gradient(90deg,${NAVY} 0%,#3A57A8 55%,${GOLD} 100%);}
    table.items th{background:transparent;color:#fff;font-weight:700;font-size:10px;letter-spacing:.5px;padding:6px 10px;text-align:left;text-shadow:0 1px 1px rgba(0,0,0,.3);}
    table.items th:first-child{border-radius:6px 0 0 6px;}
    table.items th:last-child{border-radius:0 6px 6px 0;}
    table.items td{border:none;border-bottom:1px solid #e7ebf4;padding:10px;font-size:11px;vertical-align:top;}
    .subline{font-size:10px;color:#6d7896;margin-top:2px;}
    .subline span{font-weight:700;color:${NAVY};}
    table.tot{border-collapse:collapse;width:100%;font-size:11px;}
    table.tot td{padding:6px 12px;border:none;border-bottom:1px solid #e7ebf4;}
    table.tot td:first-child{font-weight:700;color:#6d7896;text-transform:uppercase;font-size:9px;letter-spacing:.4px;}
    table.tot td:last-child{text-align:right;font-weight:700;}
    table.tot tr.gran{background:linear-gradient(90deg,${NAVY} 0%,#3A57A8 55%,${GOLD} 100%);}
    table.tot tr.gran td{background:transparent;color:#fff;border:none;font-size:13px;font-weight:800;text-shadow:0 1px 1px rgba(0,0,0,.3);padding:8px 12px;}
    table.tot tr.gran td:first-child{border-radius:6px 0 0 6px;color:#fff;}
    table.tot tr.gran td:last-child{border-radius:0 6px 6px 0;}
    .son{margin-top:16px;border-left:3px solid ${GOLD};padding:8px 14px;font-size:11px;background:#fdfaf2;border-radius:0 8px 8px 0;}
    .son strong{color:${NAVY};}
    .sig{margin-top:44px;border-top:1px solid #9aa2b5;padding-top:7px;font-size:9px;font-weight:700;color:${NAVY};text-transform:uppercase;letter-spacing:.6px;}
    .gracias{font-size:17px;font-weight:800;color:${NAVY};letter-spacing:3px;}
    .gracias span{color:${GOLD};}
    .foot{margin-top:24px;font-size:9px;color:#a6adc0;text-align:center;border-top:1px solid #edf0f7;padding-top:10px;line-height:1.6;}
    /* Margen 0 a propósito: es lo único que hace que el navegador NO imprima su
       propio encabezado y pie (la fecha a la izquierda y el título del documento
       a la derecha). El margen real de la hoja lo pone el padding del body. */
    @page{margin:0;size:A4}
    @media print{
      body{padding:12mm}
      .no-print{display:none}
      .watermark{position:fixed !important;}
      .watermark div{font-size:100px;}
      table.items tr{page-break-inside:avoid;}
    }
    /* El PDF descargado se arma desde los estilos de PANTALLA: el navegador no
       aplica @media print al capturar. De ahí que las reglas de impresión se
       repitan aquí. La única diferencia a propósito: el margen lo pone el
       generador en cada página, y no el padding del body —que en una impresión
       solo afecta al principio y al final del flujo, dejando las páginas
       intermedias pegadas al filo del papel. */
    /* El margen del PDF descargado lo pone el generador, página por página: si
       lo pusiera este padding, solo quedaría en los bordes del lienzo y la
       segunda página saldría con el texto pegado al filo del papel. */
    html.__pdf body{padding:0}
    html.__pdf .no-print{display:none}
    html.__pdf table.items tr{page-break-inside:avoid;}
    /* Al capturar, un position:fixed se pinta UNA sola vez: la firma lateral se
       ancla al documento. (La marca de BORRADOR se repite aparte, como fondo,
       porque tiene que salir en TODAS las páginas.) */
    html.__pdf .marca-lateral{position:absolute;top:50%;right:3.5mm;}

    /* ── Compactación específica de la cotización ──────────────────────────
       Solo aplica dentro de .doc-ct, para no alterar el aire de los formatos
       de OP, OCP y RC, que nadie pidió tocar. */
    .doc-ct .goldline{margin:0 0 13px;}
    .doc-ct .boxtitle{padding:10px 14px 0;}
    .doc-ct .boxbody{padding:7px 14px 10px;}
    .doc-ct .frow{margin-bottom:3px;}
    .doc-ct .frow .k{min-width:74px;}
    .doc-ct table.items{margin-top:14px;}
    .doc-ct table.items td{padding:7px 10px;}
    .doc-ct .son{margin-top:11px;padding:7px 14px;}
    .doc-ct .sig{margin-top:26px;}
    .doc-ct .foot{margin-top:15px;padding-top:8px;}
    /* Sin rótulo de texto: la línea sola encabeza el bloque, a todo el ancho. */
    .doc-ct .boxlinea{height:2.5px;background:linear-gradient(90deg,${NAVY},${GOLD});border-radius:2px;margin:12px 14px 0;}
    /* Franja de fechas: emisión, vencimiento y plazo en un mismo renglón y con
       el mismo formato — los tres son "cuándo", y merecen el mismo peso. La
       flecha va solo entre emisión y vencimiento, que es el tramo que marcan. */
    /* Cuatro celdas no caben en una línea dentro de media página. Con base 40%
       entran DOS por renglón —emisión→vencimiento arriba, plazo y entrega
       abajo— y las cuatro quedan del mismo tamaño: ninguna pesa más que otra. */
    .fechas{display:flex;flex-wrap:wrap;align-items:stretch;gap:5px;margin-bottom:7px;}
    .fechas .fcell{flex:1 1 40%;min-width:84px;background:#fff;border:1px solid #e2e7f2;border-radius:8px;padding:5px 7px;}
    .fechas .fcell.venc{border-color:${GOLD};background:#fffdf5;}
    .fechas .fcell.plazo{border-color:#bcd0f0;background:#f6f9ff;}
    .fechas .fcell.entrega{border-color:#bcd0f0;background:#f6f9ff;}
    .fechas .fk{font-size:7.5px;font-weight:800;color:#8a91a3;letter-spacing:.6px;text-transform:uppercase;white-space:nowrap;}
    .fechas .fcell.venc .fk{color:#a97e1d;}
    .fechas .fcell.plazo .fk{color:#4a6ba8;}
    .fechas .fcell.entrega .fk{color:#4a6ba8;}
    .fechas .fv{font-size:11px;font-weight:800;color:${NAVY};margin-top:1px;white-space:nowrap;}
    .fechas .fsep{display:flex;align-items:center;color:#c2c9d8;font-size:11px;font-weight:700;}
    /* El vendedor es la persona que responde por la cotización: se quedaba
       perdido como una fila más entre el lugar y la forma de entrega. Va con
       su inicial en un disco y el nombre en grande, como firma de quien cotiza. */
    .vend{display:flex;align-items:center;gap:9px;padding:6px 8px;margin-bottom:7px;
          border-radius:8px;background:linear-gradient(90deg,#f1f5fd 0%,#fbfcff 100%);
          border:1px solid #e2e7f2;}
    .vend .ini{width:26px;height:26px;border-radius:50%;flex-shrink:0;display:flex;
               align-items:center;justify-content:center;color:#fff;font-size:9.5px;font-weight:800;
               letter-spacing:.3px;background:linear-gradient(135deg,${NAVY} 0%,${GOLD} 140%);}
    .vend .vk{font-size:7.5px;font-weight:800;color:#8a91a3;letter-spacing:.6px;text-transform:uppercase;}
    .vend .vv{font-size:11.5px;font-weight:800;color:${NAVY};margin-top:1px;}
  `;
}

function encabezadoDoc(info, doc) {
  // Tres columnas en vez de dos: antes los datos de la empresa y el bloque del
  // documento se iban a los extremos y dejaban un vacío enorme en medio, con la
  // ficha de calidad apilada debajo del número (que además estiraba el alto).
  // Puesta en el centro, esa ficha ocupa el hueco y el encabezado baja de tres
  // líneas a una franja compacta.
  return `
  <div class="hdr">
    <div style="display:flex;gap:14px;align-items:center;">
      <img src="${window.LOGO}" class="hdr-logo"/>
      <div>
        <div style="font-size:15px;font-weight:800;color:${NAVY};letter-spacing:.4px;">${escapeHTML(EMPRESA.nombre)}</div>
        <div style="font-size:10px;color:#555;">NIT ${escapeHTML(EMPRESA.nit)}</div>
        <div style="font-size:10px;color:#555;">${escapeHTML(EMPRESA.direccion)}</div>
        <div style="font-size:10px;color:#555;">Tel: ${escapeHTML(EMPRESA.telefono)} • ${escapeHTML(EMPRESA.email)}</div>
      </div>
    </div>
    <table class="quality">
      <tr><td>CÓDIGO</td><td>${info.code}</td></tr>
      <tr><td>VERSIÓN</td><td>${DOC_VERSION[doc._tipo] || "01"}</td></tr>
      <tr><td>FECHA</td><td>${fmtDate(doc.fecha)}</td></tr>
    </table>
    <div style="text-align:right;">
      <div style="background:linear-gradient(90deg,${NAVY} 0%,#3A57A8 55%,${GOLD} 100%);color:white;font-size:15px;font-weight:800;padding:4px 16px;display:inline-block;border-radius:5px;letter-spacing:1px;text-shadow:0 1px 1px rgba(0,0,0,.3);">${info.label.toUpperCase()}</div>
      <div style="font-size:17px;font-weight:800;color:${GOLD};margin-top:5px;">No. ${escapeHTML(doc.numero||"")}</div>
    </div>
  </div>
  <div class="goldline"></div>`;
}

// Firma vertical del fabricante del ERP, en el borde derecho de la hoja y
// centrada entre el borde superior e inferior. La "i" y la "a" final de Axioma
// van en color: leídas solas dicen IA.
function marcaLateral() {
  return `<div class="marca-lateral" aria-hidden="true">by Ax<span class="ia">i</span>om<span class="ia">a</span> &nbsp;321-313-67-28</div>`;
}

function watermarkBorrador(doc) {
  if (doc.estado !== "borrador") return "";
  return `
    <div class="watermark" style="position:fixed;top:0;left:0;width:100%;height:100%;display:flex;align-items:center;justify-content:center;pointer-events:none;z-index:9999;overflow:hidden;">
      <div style="font-size:120px;font-weight:900;color:rgba(220,38,38,0.13);transform:rotate(-35deg);white-space:nowrap;letter-spacing:20px;text-transform:uppercase;font-family:Arial,sans-serif;">BORRADOR · BORRADOR</div>
    </div>`;
}

// ─── Nombre con el que se guarda el PDF ─────────────────────────────────────
// "CT-2324 ROBLAN COLOMBIA SAS": el consecutivo primero, que es como se busca un
// documento, y con quién es después. Sin fecha: el consecutivo ya lo identifica
// sin ambigüedad y la fecha solo alargaba el nombre.
//
// El navegador propone como nombre de archivo el <title> de la ventana, así que
// ese título ES el nombre del archivo — de ahí que se arme con cuidado.
export function nombreArchivoDoc(numero, nombreTercero) {
  // Solo se quitan los caracteres que Windows y macOS prohíben en un nombre de
  // archivo. Los acentos y la ñ se conservan: los sistemas actuales los admiten
  // y "MUÑOZ" es más útil que "MU_OZ".
  const tercero = String(nombreTercero || "SIN NOMBRE")
    .replace(/[\\/:*?"<>|]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toUpperCase()
    .slice(0, 40)
    .trim();
  return [numero, tercero].filter(Boolean).join(" ");
}

// ──────────────────────────────────────────────────────────────────────────
// INFORME FINAL DE ORDEN DE PRODUCCIÓN — resumen ejecutivo al cerrar la OP.
// Recibe un objeto ya armado por el módulo (para no importar la liquidación
// desde aquí y evitar ciclos). Estructura del informe:
//   { op, cliente, ct, items, ocps[], remisiones[], facturas[], liq, pendientes[] }
// ──────────────────────────────────────────────────────────────────────────
export function exportInformeFinalOP(data) {
  const { op, cliente = {}, ct, items = [], ocps = [], remisiones = [], facturas = [], liq = {}, pendientes = [] } = data || {};
  if (!op) { alert("OP no encontrada"); return; }

  const pct = (n) => (n == null ? "—" : Number(n).toFixed(1) + "%");
  const colorMargen = (m) => m == null ? "#64748b" : (m < 20 ? "#dc2626" : m < 40 ? "#d97706" : "#059669");
  const chip = (txt, color) => `<span style="background:${color}1a;color:${color};padding:2px 9px;border-radius:20px;font-size:9.5px;font-weight:800;white-space:nowrap;">${escapeHTML(txt)}</span>`;

  // ── Cadena de trazabilidad (CT → OP → OCP → RS → Factura) ─────────────────
  const eslabon = (icono, titulo, sub, color) => `
    <div style="flex:1;min-width:96px;text-align:center;">
      <div style="background:${color};color:#fff;border-radius:8px;padding:7px 6px;">
        <div style="font-size:15px;line-height:1;">${icono}</div>
        <div style="font-size:11px;font-weight:800;margin-top:3px;">${escapeHTML(titulo)}</div>
      </div>
      <div style="font-size:8.5px;color:#555;margin-top:3px;line-height:1.35;">${sub}</div>
    </div>`;
  const flecha = `<div style="align-self:flex-start;margin-top:14px;color:${GOLD};font-weight:800;font-size:15px;">→</div>`;
  const cadena = [
    ct ? eslabon("📄", ct.numero, fmtDate(ct.fecha), "#2B4A82") : null,
    eslabon("🏭", op.numero, fmtDate(op.fecha), "#4C5FA8"),
    ocps.length ? eslabon("🛒", ocps.length + " OCP", ocps.map(o => o.numero).filter(Boolean).slice(0,3).join(", ") || "compras", "#7c3aed") : null,
    remisiones.length ? eslabon("📮", remisiones.length + " RS", remisiones.map(r => r.numero).slice(0,3).join(", "), "#0E7490") : null,
    facturas.length ? eslabon("💰", facturas.length + " Fact.", facturas.map(f => f.numero).slice(0,3).join(", "), "#059669") : null
  ].filter(Boolean);
  const cadenaHTML = cadena.reduce((acc, e, i) => acc + (i ? flecha : "") + e, "");

  // ── Ítems producidos ──────────────────────────────────────────────────────
  const itemsHTML = items.map((it, i) => `
    <tr>
      <td style="text-align:center;width:34px;font-weight:700;color:${NAVY};">${i+1}</td>
      <td><strong>${escapeHTML(it.articulo || "")}</strong>${it.descripcion ? `<div style="color:#555;font-size:10px;margin-top:1px;">${escapeHTML(it.descripcion)}</div>` : ""}</td>
      <td style="text-align:right;width:60px;">${it.cantidad || 0}</td>
      <td style="text-align:right;width:96px;">${fmtCOP(it.precio_unitario)}</td>
      <td style="text-align:right;width:104px;font-weight:700;">${fmtCOP((Number(it.cantidad)||0)*(Number(it.precio_unitario)||0))}</td>
    </tr>`).join("");

  // ── Costos / OCP ─────────────────────────────────────────────────────────
  const ocpHTML = ocps.length ? ocps.map(o => `
    <tr>
      <td style="font-weight:700;color:#7c3aed;">${escapeHTML(o.numero || "—")}</td>
      <td>${escapeHTML(o.proveedor || "—")}</td>
      <td style="text-align:right;">${fmtCOP(o.total)}</td>
      <td style="text-align:right;color:#059669;">${fmtCOP(o.pagado)}</td>
      <td style="text-align:right;color:${o.saldo>0?"#dc2626":"#64748b"};font-weight:${o.saldo>0?"700":"400"};">${fmtCOP(o.saldo)}</td>
      <td style="text-align:center;">${o.saldo>0?chip("Por pagar","#d97706"):chip("Pagada","#059669")}</td>
    </tr>`).join("") : `<tr><td colspan="6" style="text-align:center;color:#777;padding:10px;">Sin órdenes de compra asociadas.</td></tr>`;

  // ── Entregas y facturación ────────────────────────────────────────────────
  const remiHTML = remisiones.length ? remisiones.map(r => `
    <tr>
      <td style="font-weight:700;color:#0E7490;">${escapeHTML(r.numero || "—")}</td>
      <td>${escapeHTML(fmtDate(r.fecha))}</td>
      <td style="text-align:center;">${r.estado==="facturada"?chip("Facturada","#059669"):chip("Sin facturar","#d97706")}</td>
      <td>${r.facturaNumero ? escapeHTML(r.facturaNumero) : "—"}</td>
    </tr>`).join("") : `<tr><td colspan="4" style="text-align:center;color:#777;padding:10px;">Sin remisiones (entregas) registradas.</td></tr>`;

  // ── Pendientes ────────────────────────────────────────────────────────────
  const pendHTML = pendientes.length
    ? `<div style="border-left:3px solid #d97706;background:#fffbeb;border-radius:0 8px 8px 0;padding:11px 14px;">
        <div style="font-size:11px;font-weight:800;color:#92400e;margin-bottom:6px;">⚠️ PENDIENTES</div>
        <ul style="margin:0;padding-left:18px;font-size:11px;color:#78350f;line-height:1.7;">
          ${pendientes.map(p => `<li>${escapeHTML(p)}</li>`).join("")}
        </ul>
      </div>`
    : `<div style="border-left:3px solid #059669;background:#ecfdf5;border-radius:0 8px 8px 0;padding:11px 14px;">
        <div style="font-size:11px;font-weight:800;color:#065f46;">✅ SIN PENDIENTES</div>
        <div style="font-size:11px;color:#047857;margin-top:3px;">La orden quedó completamente cerrada: entregada, facturada y con los costos pagados.</div>
      </div>`;

  const mFinal = liq.margen, mIni = liq.margenInicial;
  const deltaMargen = (mFinal != null && mIni != null) ? (mFinal - mIni) : null;

  const info = DOC_INFO.OP;
  const filename = "Informe Final " + nombreArchivoDoc(op.numero, cliente.nombre);
  const body = `
  ${encabezadoDoc(info, { ...op, _tipo:"OP" }).replace(info.label.toUpperCase(), "INFORME FINAL DE OP")}

  <div style="display:grid;grid-template-columns:1.4fr 1fr;gap:14px;margin-bottom:4px;">
    <div class="box">
      <div class="boxtitle">ORDEN DE PRODUCCIÓN</div>
      <div class="boxbody">
        ${filaCampo("N° OP", op.numero)}
        ${filaCampo("Cliente", cliente.nombre)}
        ${filaCampo("NIT", cliente.nit)}
        ${filaCampo("Asesor", op.asesor_comercial)}
        ${filaCampo("N° OC del cliente", op.numero_oc_cliente)}
        ${filaCampo("Fecha OP", fmtDate(op.fecha))}
      </div>
    </div>
    <div class="box">
      <div class="boxtitle">RESULTADO</div>
      <div class="boxbody" style="text-align:center;">
        <div style="font-size:9px;font-weight:700;color:${NAVY};letter-spacing:.6px;">MARGEN DE UTILIDAD FINAL</div>
        <div style="font-size:30px;font-weight:800;color:${colorMargen(mFinal)};line-height:1.1;margin:2px 0;">${pct(mFinal)}</div>
        <div style="font-size:9.5px;color:#555;">
          Estimado inicial: <b>${pct(mIni)}</b>
          ${deltaMargen != null ? ` · <span style="color:${deltaMargen>=0?"#059669":"#dc2626"};font-weight:700;">${deltaMargen>=0?"▲":"▼"} ${Math.abs(deltaMargen).toFixed(1)} pts</span>` : ""}
        </div>
        <div style="margin-top:8px;font-size:12px;font-weight:800;color:${liq.utilidad>=0?"#059669":"#dc2626"};">Utilidad: ${fmtCOP(liq.utilidad)}</div>
      </div>
    </div>
  </div>

  <div class="box" style="margin-bottom:4px;">
    <div class="boxtitle">TRAZABILIDAD</div>
    <div class="boxbody" style="display:flex;gap:8px;align-items:stretch;overflow-x:auto;">${cadenaHTML}</div>
  </div>

  <table class="items">
    <thead><tr><th style="text-align:center;">#</th><th>PRODUCTO</th><th style="text-align:right;">CANT</th><th style="text-align:right;">P. UNIT</th><th style="text-align:right;">TOTAL</th></tr></thead>
    <tbody>${itemsHTML}</tbody>
  </table>

  <div style="display:grid;grid-template-columns:1fr 1fr;gap:14px;margin-top:16px;">
    <div class="box">
      <div class="boxtitle">RESUMEN FINANCIERO</div>
      <div class="boxbody" style="font-size:11px;">
        <div class="frow"><div class="k">Venta total</div><div style="font-weight:700;">${fmtCOP(liq.ventaTotal)}</div></div>
        <div class="frow"><div class="k">Costo de compras (OCP)</div><div>${fmtCOP(liq.costoOcpTotal)}</div></div>
        <div class="frow"><div class="k">Otros costos asociados</div><div>${fmtCOP(liq.otrosCostosTotal)}</div></div>
        <div class="frow" style="border-top:1px solid #e7ebf4;margin-top:4px;padding-top:4px;"><div class="k">Costo total</div><div style="font-weight:700;">${fmtCOP(liq.costoTotal)}</div></div>
        <div class="frow"><div class="k">Utilidad</div><div style="font-weight:800;color:${liq.utilidad>=0?"#059669":"#dc2626"};">${fmtCOP(liq.utilidad)}</div></div>
        <div class="frow" style="margin-top:5px;"><div class="k" style="font-size:9.5px;">Costo pagado</div><div style="color:#059669;">${fmtCOP(liq.costoPagado)}</div></div>
        <div class="frow"><div class="k" style="font-size:9.5px;">Costo por pagar</div><div style="color:${liq.costoPorPagar>0?"#dc2626":"#64748b"};">${fmtCOP(liq.costoPorPagar)}</div></div>
      </div>
    </div>
    <div style="display:flex;flex-direction:column;gap:10px;">
      ${pendHTML}
    </div>
  </div>

  <div class="box" style="margin-top:14px;">
    <div class="boxtitle">ÓRDENES DE COMPRA (OCP)</div>
    <div class="boxbody" style="padding-top:6px;">
      <table style="width:100%;border-collapse:collapse;font-size:10px;">
        <thead><tr style="color:${NAVY};text-align:left;border-bottom:2px solid ${GRIS_LINEA};">
          <th style="padding:4px 6px;">OCP</th><th style="padding:4px 6px;">Proveedor</th><th style="padding:4px 6px;text-align:right;">Total</th><th style="padding:4px 6px;text-align:right;">Pagado</th><th style="padding:4px 6px;text-align:right;">Saldo</th><th style="padding:4px 6px;text-align:center;">Estado</th>
        </tr></thead>
        <tbody>${ocpHTML}</tbody>
      </table>
    </div>
  </div>

  <div class="box" style="margin-top:14px;">
    <div class="boxtitle">ENTREGAS Y FACTURACIÓN</div>
    <div class="boxbody" style="padding-top:6px;">
      <table style="width:100%;border-collapse:collapse;font-size:10px;">
        <thead><tr style="color:${NAVY};text-align:left;border-bottom:2px solid ${GRIS_LINEA};">
          <th style="padding:4px 6px;">Remisión</th><th style="padding:4px 6px;">Fecha</th><th style="padding:4px 6px;text-align:center;">Estado</th><th style="padding:4px 6px;">Factura</th>
        </tr></thead>
        <tbody>${remiHTML}</tbody>
      </table>
    </div>
  </div>

  <div style="margin-top:16px;font-size:9px;color:#8a93a8;text-align:center;">
    Informe generado el ${fmtDate(today())} · ${escapeHTML(EMPRESA.nombre)} · Documento interno de gestión, sin valor fiscal.
  </div>`;

  abrirVentanaDoc(filename, body);
}

// ─── La ventana de un documento: se ve en pantalla y se descarga ────────────
// Todo documento que emite Aura pasa por aquí. El botón principal DESCARGA el
// archivo .pdf con su nombre ya puesto; imprimir queda como opción secundaria
// (para el papel que se firma). Antes el único botón abría el diálogo de
// impresión, así que llevarse el archivo exigía saber que dentro de ese diálogo
// hay un "Guardar como PDF" escondido entre las impresoras.
//
// El generador (html2pdf) se carga en ESTA ventana, no en el bundle: pesa cerca
// de un mega y solo hace falta cuando alguien pide un documento.
const CDN_HTML2PDF = "https://cdn.jsdelivr.net/npm/html2pdf.js@0.14.0/dist/html2pdf.bundle.min.js";
const SRI_HTML2PDF = "sha384-EaWTV/aVUkLz3tfwg3+5ycX7Q/d9ET9ruOKUgUuFIRUCfzHO1eo2J62a844iWPmY";

// `margenMM` es el margen que el generador deja en CADA página del PDF. Por
// defecto 12, el de los formatos corporativos (CT/OP/OCP/RC); la factura, la
// nota crédito y los certificados usan 15, el que ya tenían en @page.
function barraYGenerador(filename, margenMM) {
  return `
  <div class="no-print" id="__barra" style="position:fixed;bottom:14px;right:14px;display:flex;gap:8px;z-index:10000;">
    <button id="__imprimir" title="Imprimir en papel"
      style="padding:10px 16px;background:#fff;color:${NAVY};border:1px solid ${NAVY};border-radius:6px;cursor:pointer;font-weight:700;box-shadow:0 4px 14px rgba(0,0,0,.18);">🖨️ Imprimir</button>
    <button id="__descargar"
      style="padding:10px 20px;background:${NAVY};color:white;border:none;border-radius:6px;cursor:pointer;font-weight:700;box-shadow:0 4px 14px rgba(0,0,0,.25);border-bottom:3px solid ${GOLD};">📥 Descargar PDF</button>
  </div>
  <script>
  (function(){
    var nombre = ${JSON.stringify(filename)};
    var margen = ${Number(margenMM) || 0};
    var barra = document.getElementById("__barra");
    var btn = document.getElementById("__descargar");
    document.getElementById("__imprimir").onclick = function(){ window.print(); };

    // El generador se baja al pulsar el botón, NO con la ventana. Dos razones:
    // pesa casi un mega y quien solo quiere mirar el documento no tiene por qué
    // esperarlo; y sobre todo, Chrome BLOQUEA los <script src> de otro dominio
    // que se inyectan con document.write —que es como se escribe esta ventana—,
    // así que un <script> en el HTML simplemente no cargaba. Un script añadido
    // al DOM sí carga.
    var promesa = null;
    function cargarGenerador() {
      if (window.html2pdf) return Promise.resolve(window.html2pdf);
      if (promesa) return promesa;
      promesa = new Promise(function(resolver, rechazar) {
        var s = document.createElement("script");
        s.src = ${JSON.stringify(CDN_HTML2PDF)};
        s.integrity = ${JSON.stringify(SRI_HTML2PDF)};
        s.crossOrigin = "anonymous";
        s.onload = function() {
          if (window.html2pdf) resolver(window.html2pdf);
          else rechazar(new Error("el generador cargó incompleto"));
        };
        s.onerror = function() { rechazar(new Error("no se pudo descargar el generador")); };
        document.head.appendChild(s);
      });
      // Un fallo no deja la promesa en caché: el siguiente clic reintenta.
      promesa.catch(function(){ promesa = null; });
      return promesa;
    }

    // En el teléfono el archivo sale por la hoja de COMPARTIR (o se descarga),
    // siempre con su nombre: "CT-2324 ROBLAN COLOMBIA SAS.pdf".
    function descargarArchivo(archivo) {
      var url = URL.createObjectURL(archivo);
      var a = document.createElement("a");
      a.href = url; a.download = archivo.name; a.style.display = "none";
      document.body.appendChild(a); a.click();
      setTimeout(function(){ URL.revokeObjectURL(url); a.remove(); }, 4000);
    }
    function compartirODescargar(archivo) {
      return navigator.share({ files: [archivo], title: nombre }).catch(function(err){
        // Cerrar la hoja de compartir sin elegir nada no es un error.
        if (err && err.name === "AbortError") return;
        // Un toque NUEVO que el sistema no acepta para compartir: se descarga,
        // que también deja el archivo con su nombre.
        descargarArchivo(archivo);
      });
    }
    // El PDF ya armado, esperando el segundo toque (ver más abajo).
    var listo = null;

    btn.onclick = async function(){
      if (listo) { compartirODescargar(listo); return; }
      var textoAntes = btn.textContent;
      btn.disabled = true; btn.textContent = "⏳ Preparando…";
      try { await cargarGenerador(); }
      catch (err) {
        // Sin el generador no hay archivo que ofrecer. En vez de dejar el botón
        // muerto se abre la impresión, que también sabe guardar en PDF, y se
        // dice por qué.
        btn.disabled = false; btn.textContent = textoAntes;
        alert("No se pudo cargar el generador de PDF (revisa la conexión).\\n\\nSe abre la impresión: elige «Guardar como PDF» como destino.");
        window.print(); return;
      }
      btn.textContent = "⏳ Generando…";
      // La barra no puede salir dentro del archivo, y el PDF se arma desde los
      // estilos de PANTALLA: el navegador no aplica @media print aquí. La clase
      // __pdf trae esas mismas reglas (margen del body, ocultar lo que no se
      // imprime) para que el archivo salga igual que el impreso.
      barra.style.display = "none";
      document.documentElement.classList.add("__pdf");

      // La marca de BORRADOR tiene que salir en TODAS las páginas: un borrador
      // que parece definitivo a partir de la hoja 2 es justo lo que la marca
      // existe para evitar. El generador corta un lienzo único, así que un
      // elemento (fijo o absoluto) se pinta una vez; un FONDO repetido cada
      // alto de página sí se reparte por todas.
      var marca = document.querySelector(".watermark");
      var fondoAntes = document.body.style.background;
      if (marca) {
        var ancho = document.body.scrollWidth;
        // Proporción de la hoja útil: A4 menos el margen que pone el generador.
        var altoPagina = Math.round(ancho * (297 - 2 * margen) / (210 - 2 * margen));
        var svg = '<svg xmlns="http://www.w3.org/2000/svg" width="' + ancho + '" height="' + altoPagina + '">' +
          '<text x="50%" y="50%" fill="rgba(220,38,38,0.13)" font-family="Arial,sans-serif"' +
          ' font-size="100" font-weight="900" letter-spacing="18" text-anchor="middle"' +
          ' dominant-baseline="middle" transform="rotate(-35 ' + (ancho/2) + ' ' + (altoPagina/2) + ')">' +
          'BORRADOR · BORRADOR</text></svg>';
        document.body.style.backgroundImage = 'url("data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg) + '")';
        document.body.style.backgroundRepeat = "repeat-y";
        document.body.style.backgroundPosition = "top center";
        document.body.style.backgroundSize = "100% " + altoPagina + "px";
        marca.style.display = "none";
      }

      var limpiar = function(){
        document.documentElement.classList.remove("__pdf");
        if (marca) { marca.style.display = ""; document.body.style.background = fondoAntes; }
        barra.style.display = "";
        btn.disabled = false; btn.textContent = textoAntes;
      };
      var trabajo = window.html2pdf().set({
        margin: margen,
        filename: nombre + ".pdf",
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, backgroundColor: "#ffffff", logging: false },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        // Nada de esto se puede cortar por la mitad: una fila de la tabla, el
        // pie fiscal o una línea de firma partidos entre dos páginas se leen
        // como un documento roto.
        pagebreak: { mode: ["css","legacy"], avoid: ["tr", ".foot", ".sig"] }
      }).from(document.body);
      var fallo = function(err){
        limpiar();
        console.error(err);
        alert("No se pudo generar el archivo. Se abre la impresión como alternativa: elige «Guardar como PDF».");
        window.print();
      };
      // En el teléfono, "descargar" no es lo natural: lo natural es COMPARTIR
      // (WhatsApp, correo, guardar en Archivos). Si el sistema sabe compartir
      // archivos se arma el PDF y se abre la hoja de compartir; si la persona
      // la cierra sin elegir nada, no pasa nada. Donde no se pueda, descarga.
      var tactil = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) || navigator.maxTouchPoints > 1;
      var puedeCompartir = tactil && navigator.share && navigator.canShare && typeof window.File === "function";
      if (puedeCompartir) {
        trabajo.outputPdf("blob").then(function(blob){
          var archivo = new window.File([blob], nombre + ".pdf", { type: "application/pdf" });
          if (!navigator.canShare({ files: [archivo] })) { descargarArchivo(archivo); return; }
          return navigator.share({ files: [archivo], title: nombre }).catch(function(err){
            if (err && err.name === "AbortError") return;
            // El iPhone solo deja compartir justo después de un toque, y armar
            // el PDF tarda unos segundos: para entonces el permiso del primer
            // toque ya venció (NotAllowedError). Antes eso caía a «Imprimir» y
            // el archivo salía sin su nombre. Ahora el PDF queda listo y el
            // botón pide un segundo toque, que sí abre la hoja de compartir.
            listo = archivo;
            throw { pideToque: true };
          });
        }).then(limpiar, function(err){
          if (err && err.pideToque) {
            limpiar();
            btn.textContent = "📤 Guardar «" + nombre + ".pdf»";
            return;
          }
          fallo(err);
        });
      } else {
        trabajo.save().then(limpiar, fallo);
      }
    };
  })();
  <\/script>`;
}

function abrirVentanaDoc(filename, bodyHTML, opts = {}) {
  const estilos = opts.estilos || estilosDoc();
  const margenMM = opts.margenMM != null ? opts.margenMM : 12;
  const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>${escapeHTML(filename)}</title>
  <style>${estilos}</style></head><body>
  ${bodyHTML}
  ${barraYGenerador(filename, margenMM)}
  </body></html>`;
  // En el celular y en la app instalada el documento se muestra ADENTRO (ver
  // ui/visorDoc.js): el iPhone instalado como app bloquea las ventanas
  // emergentes o las abre fuera, en Safari.
  if (abrirDocumentoEnApp(filename, { html })) return;
  // El nombre de la ventana es un identificador interno del navegador, no el del
  // archivo: se pasa sin espacios ni acentos. El nombre bonito va en el título
  // (que es lo que el navegador propone al imprimir) y en el filename del PDF.
  const w = window.open("", filename.replace(/[^\w-]+/g, "_"), "width=900,height=750");
  if (!w) { alert("Permite ventanas emergentes en tu navegador"); return; }
  w.document.write(html);
  w.document.close();
  w.document.title = filename;
}

function pieFiscal(info, doc) {
  return `<div class="foot">Responsables de IVA • No somos grandes contribuyentes • No somos autoretenedores<br>${escapeHTML(EMPRESA.nombre)} • NIT ${escapeHTML(EMPRESA.nit)} • ${info.code} v${DOC_VERSION[doc._tipo] || "01"}</div>`;
}

function filaCampo(k, v) {
  return v ? `<div class="frow"><div class="k">${k}</div><div>${escapeHTML(v)}</div></div>` : "";
}

// Iniciales para el disco del vendedor: las dos primeras palabras del nombre.
// En "Natalia Idárraga Toro" son el nombre y el primer apellido → NI. Se
// descartan las partículas ("de", "del", "la"), que no identifican a nadie y
// desplazarían a la letra que sí lo hace.
function iniciales(nombre) {
  const PARTICULAS = ["de", "del", "la", "las", "los", "y", "da", "van", "von"];
  const partes = String(nombre || "").trim().split(/\s+/)
    .filter(p => p && !PARTICULAS.includes(p.toLowerCase()));
  return partes.slice(0, 2).map(p => p[0]).join("").toUpperCase();
}

// Detalle del ítem en líneas (Referencia / Color / Logo / Impresión / Ubicación / Tinta),
// como en los formatos de Excel donde cada atributo va debajo de la descripción.
// Lo que TRAE un kit, para imprimirlo bajo el artículo. El cliente compra un
// "Set escolar" y tiene derecho a saber que son 2 cuadernos y 3 lápices; y a
// quien despacha le dice qué meter en la caja.
function composicionKitHTML(it) {
  const prod = productoPorNombre(it && it.articulo);
  if (!esProductoKit(prod)) return "";
  return `<div class="subline" style="margin-top:2px;"><span>Contiene:</span> ${escapeHTML(descripcionKitCatalogo(prod))}</div>`;
}

function detalleItem(it, tipo) {
  const lineas = [];
  // La referencia es interna (control de inventario y compras): se imprime en
  // las órdenes y remisiones, pero NO en la cotización que ve el cliente.
  if (it.referencia && tipo !== "CT") lineas.push(["Referencia", it.referencia]);
  if (it.color) lineas.push(["Color", it.color]);
  if (it.logo) lineas.push(["Logo", it.logo]);
  if (it.impresion && tipo !== "CT") lineas.push(["Impresión", it.impresion]);
  if (tipo === "OCP" && it.ubicacion) lineas.push(["Ubicación", it.ubicacion]);
  if (tipo === "OP" && it.tinta) lineas.push(["Tinta", it.tinta]);
  return lineas.map(([k, v]) => `<div class="subline"><span>${k}:</span> ${escapeHTML(v)}</div>`).join("")
       + composicionKitHTML(it);
}

function bloqueFacturarA() {
  return `
    <div class="box">
      <div class="boxtitle">FACTURAR A</div>
      <div class="boxbody" style="font-size:11px;line-height:1.5;">
        <strong>${escapeHTML(EMPRESA.nombre)}</strong><br>
        NIT ${escapeHTML(EMPRESA.nit)}<br>
        ${escapeHTML(EMPRESA.direccion)}<br>
        Tel: ${escapeHTML(EMPRESA.telefono)}<br>
        ${escapeHTML(EMPRESA.email)}
      </div>
    </div>`;
}

function bloqueEnviarA(doc, fallback) {
  const texto = doc.enviar_a || fallback || "";
  return `
    <div class="box">
      <div class="boxtitle">ENVIAR A</div>
      <div class="boxbody" style="font-size:11px;line-height:1.5;white-space:pre-line;">${escapeHTML(texto) || "—"}</div>
    </div>`;
}

// Escalas de cantidad de un ítem (el mismo artículo a 100, 500, 1000…).
function escalasCT(it) {
  return (it && Array.isArray(it._escalas) ? it._escalas : [])
    .filter(e => (parseNumber(e.cantidad) || 0) > 0);
}

// ¿La cotización tiene alguna línea cotizada por cantidades?
function hayEscalas(doc) {
  return (doc.items || []).some(it => escalasCT(it).length > 0);
}

function totalesDoc(doc, opts = {}) {
  // Con líneas cotizadas por cantidad no hay un total único: el valor depende
  // de la cantidad que elija el cliente. Imprimir una suma ahí sería inventar
  // una cifra (o dejar fuera esas líneas sin decirlo).
  if (hayEscalas(doc)) {
    const conCantidad = (doc.items || []).filter(it => escalasCT(it).length === 0);
    const sub = conCantidad.reduce((a, it) => a + (parseNumber(it.total) || 0), 0);
    return `
      <table class="tot">
        ${sub > 0 ? `
          <tr><td>SUBTOTAL ÍTEMS CON CANTIDAD FIJA</td><td>${fmtCOP(sub)}</td></tr>
          ${filasIVA(conCantidad)}
          <tr class="gran"><td>TOTAL PARCIAL</td><td>${fmtCOP(sub + desgloseIVA(conCantidad).iva)}</td></tr>` : ""}
      </table>
      <div style="margin-top:8px;background:#f7f9fd;border-left:3px solid ${NAVY};border-radius:0 6px 6px 0;
                  padding:8px 11px;font-size:9.5px;color:#1f2937;line-height:1.5;">
        <strong style="color:${NAVY};">Precios por cantidad.</strong>
        ${sub > 0 ? "Los ítems cotizados por cantidad no están sumados arriba: " : ""}el
        valor final depende de la cantidad que se confirme en cada referencia.
        Los precios son unitarios y <strong>no incluyen IVA</strong>.
      </div>`;
  }
  const filas = [];
  if (opts.bruto) {
    filas.push(["TOTAL BRUTO", fmtCOP(doc.subtotal)]);
    filas.push(["DESCUENTO COMERCIAL", fmtCOP(0)]);
  }
  filas.push(["SUBTOTAL", fmtCOP(doc.subtotal)]);
  const filasHTML = filas.map(([k, v]) => `<tr><td>${k}</td><td>${v}</td></tr>`).join("");
  // Una orden INFORMATIVA va sin IVA aunque sus líneas lleguen sin la marca
  // (una fila sincronizada por un cliente viejo): se imprimía con IVA 19% sobre
  // un total que no lo tenía.
  const itemsIVA = doc._informativa ? itemsSinIVA(doc.items) : doc.items;
  return `
    <table class="tot">
      ${filasHTML}
      ${filasIVA(itemsIVA, doc.iva)}
      <tr class="gran"><td>TOTAL</td><td>${fmtCOP(doc.total)}</td></tr>
    </table>`;
}

// El sello que va junto al artículo cuando su línea NO lleva el IVA normal. Sin
// esto, un documento con líneas gravadas y excluidas mezcladas cuadra en los
// totales pero no dice CUÁL línea no llevó impuesto.
function selloIVA(it) {
  const r = regimenDeLinea(it);
  if (r.tipo === "gravado" && r.pct === ivaPct()) return "";
  const texto = r.tipo === "exento" ? "Exento · art. 477 E.T."
    : r.tipo === "excluido" ? "Excluido · art. 424 E.T."
    : `IVA ${r.pct}%`;
  return `<span style="margin-left:6px;background:#fef3c7;color:#92400e;padding:1px 6px;
            border-radius:5px;font-size:8px;font-weight:700;white-space:nowrap;">${texto}</span>`;
}

// ─── El IVA en el documento impreso ─────────────────────────────────────────
// Con un solo régimen se imprime la línea de siempre ("IVA 19%"). Cuando el
// documento mezcla tarifas —o lleva un bien exento o excluido— hay que mostrar
// CADA BASE por separado: es lo que permite al cliente (y a su contador)
// verificar el impuesto, y lo que distingue una base exenta del art. 477 de una
// excluida del art. 424, que en pesos se ven iguales (las dos en cero).
function filasIVA(items, ivaGuardado) {
  return renglonesIVA(items, ivaGuardado)
    .map(r => `<tr><td>${escapeHTML(r.lbl.toUpperCase())}</td><td>${fmtCOP(r.valor)}</td></tr>`).join("");
}

// --------------------------------------------------------------------------
// COTIZACIÓN (CT) — MTHC-010
// --------------------------------------------------------------------------
function cuerpoCT(info, doc, cliente, fotos = new Map()) {
  // La cotización guarda una copia del contacto al emitirse. Las cotizaciones
  // anteriores a esa copia no la tienen: para esas se recurre al contacto que
  // el cliente tenga marcado como imprimible en su ficha.
  const contacto = doc._contacto || contactoImprimible(cliente) || {};
  const vencimiento = doc.fecha_vigencia
    || ((doc._dias_vigencia !== "" && doc._dias_vigencia != null) ? addDias(doc.fecha, doc._dias_vigencia) : null);
  // Plazo de pago, en la misma franja que las fechas y con el mismo formato.
  // La celda es estrecha, así que se imprime la forma corta ("30 días"). Ojo:
  // condiciones_pago se autogenera a partir de dias_pago ("Un mes (30 días)"),
  // y esa versión larga no cabe; pero si el asesor la reescribió a mano, ese
  // texto es información propia y manda sobre el plazo de la ficha.
  const diasPlazo = cliente && cliente.dias_pago != null && cliente.dias_pago !== ""
    ? Number(cliente.dias_pago) : null;
  const corto = diasPlazo == null ? "" : (diasPlazo === 0 ? "Contado" : diasPlazo + " días");
  const AUTOGENERADO = /^(Contado|Una semana \(7 días\)|Un mes \(30 días\)|Dos meses \(60 días\)|Tres meses \(90 días\)|\d+ días)$/;
  const textoPlazo = (doc.condiciones_pago && !AUTOGENERADO.test(doc.condiciones_pago.trim()))
    ? doc.condiciones_pago
    : (corto || doc.condiciones_pago || "");
  // Tiempo de entrega: obligatorio al emitir y siempre expresado en días hábiles.
  const textoEntrega = textoTiempoEntrega(doc.tiempo_entrega);

  // Lado de la miniatura, ajustable desde Configuración → Formato de documentos.
  const ladoImg = getFormatoDoc().imagenCT;
  const anchoColImg = ladoImg + 12;   // la celda deja un respiro alrededor

  const itemsHTML = (doc.items || []).map((it, i) => {
    // La foto viene ya EMBEBIDA (ver embebidasParaDocumento): la cotización se
    // abre en otra ventana y se imprime, así que no puede depender de una URL
    // que caduque ni de la red en ese instante.
    const foto = fotos.get(it.articulo) || "";
    const imgCell = foto
      ? `<img src="${foto}" style="max-width:${ladoImg}px;max-height:${ladoImg}px;width:auto;height:auto;object-fit:contain;border-radius:6px;border:1px solid ${GRIS_LINEA};display:block;margin:0 auto;"/>`
      : "";
    // El artículo cotizado a varias cantidades: la foto y la descripción se
    // imprimen UNA sola vez y al frente va una línea por cantidad, como si
    // fueran ítems distintos pero sin repetir nada.
    const esc = escalasCT(it);
    const celdasCant = esc.length
      ? `
      <td style="text-align:right;width:38px;padding-left:4px;padding-right:4px;">
        ${esc.map(e => `<div style="padding:2px 0;">${parseNumber(e.cantidad) || 0}</div>`).join("")}
      </td>
      <td style="text-align:right;width:78px;padding-left:5px;padding-right:5px;white-space:nowrap;">
        ${esc.map(e => `<div style="padding:2px 0;">${fmtCOP(e.precio_unitario)}</div>`).join("")}
      </td>
      <td style="text-align:right;width:86px;padding-left:5px;padding-right:5px;font-weight:700;white-space:nowrap;">
        ${esc.map(e => `<div style="padding:2px 0;">${fmtCOP((parseNumber(e.cantidad) || 0) * (parseNumber(e.precio_unitario) || 0))}</div>`).join("")}
      </td>`
      : `
      <td style="text-align:right;width:38px;padding-left:4px;padding-right:4px;">${it.cantidad || 0}</td>
      <td style="text-align:right;width:78px;padding-left:5px;padding-right:5px;white-space:nowrap;">${fmtCOP(it.precio_unitario)}</td>
      <td style="text-align:right;width:86px;padding-left:5px;padding-right:5px;font-weight:700;white-space:nowrap;">${fmtCOP(it.total)}</td>`;
    return `<tr>
      <td style="text-align:center;width:30px;padding-left:4px;padding-right:4px;font-weight:700;color:${NAVY};">${i + 1}</td>
      <td style="text-align:center;width:${anchoColImg}px;padding:5px;">${imgCell}</td>
      <td><strong>${escapeHTML(it.articulo)}</strong>${selloIVA(it)}${it.descripcion ? `<div style="margin-top:2px;">${escapeHTML(it.descripcion)}</div>` : ""}${detalleItem(it, "CT")}${
        esc.length ? `<div style="margin-top:4px;font-size:9.5px;color:${NAVY};font-weight:700;">Precio según cantidad</div>` : ""}</td>
      <td style="width:62px;padding-left:6px;padding-right:6px;">${escapeHTML(it.impresion || "")}</td>
      ${celdasCant}
    </tr>`;
  }).join("");

  return `
  <div class="doc-ct">
  ${encabezadoDoc(info, doc)}
  <div style="display:grid;grid-template-columns:1fr 1.15fr;gap:12px;">
    <div class="box">
      <div class="boxlinea"></div>
      <div class="boxbody">
        ${filaCampo("Cliente", cliente.nombre)}
        ${filaCampo("Contacto", contacto.nombre)}
        ${filaCampo("Cargo", contacto.cargo)}
        ${/* Con un contacto explícito se imprime SU correo (nunca el general del
             cliente, que sería el de otra persona). Solo las CT antiguas sin
             contacto guardado caen al correo del cliente. */ ""}
        ${filaCampo("E-mail", doc._contacto ? (contacto.email || "") : (contacto.email || cliente.email))}
        ${filaCampo("Ciudad", cliente.ciudad)}
      </div>
    </div>
    <div class="box">
      <div class="boxlinea"></div>
      <div class="boxbody">
        <div class="fechas">
          <div class="fcell">
            <div class="fk">Emisión</div>
            <div class="fv">${fmtDate(doc.fecha)}</div>
          </div>
          ${vencimiento ? `
            <div class="fsep">→</div>
            <div class="fcell venc">
              <div class="fk">Vence</div>
              <div class="fv">${fmtDate(vencimiento)}</div>
            </div>` : ""}
          ${textoPlazo ? `
            <div class="fcell plazo">
              <div class="fk">Plazo de pago</div>
              <div class="fv">${escapeHTML(textoPlazo)}</div>
            </div>` : ""}
          ${textoEntrega ? `
            <div class="fcell entrega">
              <div class="fk">Tiempo de entrega</div>
              <div class="fv">${escapeHTML(textoEntrega)}</div>
            </div>` : ""}
        </div>
        ${doc.asesor_comercial ? `
          <div class="vend">
            <div class="ini">${escapeHTML(iniciales(doc.asesor_comercial))}</div>
            <div style="min-width:0;">
              <div class="vk">Vendedor</div>
              <div class="vv">${escapeHTML(doc.asesor_comercial)}</div>
            </div>
          </div>` : ""}
        ${filaCampo("Lugar de entrega", doc.lugar_entrega)}
        ${filaCampo("Forma de pago", doc.forma_pago)}
      </div>
    </div>
  </div>
  <table class="items">
    <thead><tr>
      ${/* Sin nowrap en los dos últimos: obligaba a reservarles ~100px de ancho
           y ese espacio salía de la descripción, que quedaba en una tira de una
           palabra por renglón. Partidos en dos renglones se leen igual. */ ""}
      <th style="text-align:center;padding-left:4px;padding-right:4px;">ITEM</th><th style="text-align:center;">IMAGEN</th>
      <th>DESCRIPCIÓN</th><th style="padding-left:6px;padding-right:6px;">IMPRESIÓN</th><th style="text-align:right;padding-left:4px;padding-right:4px;">CANT</th>
      <th style="text-align:right;padding-left:5px;padding-right:5px;">PRECIO UNIT.</th><th style="text-align:right;padding-left:5px;padding-right:5px;">PRECIO TOTAL</th>
    </tr></thead>
    <tbody>${itemsHTML}</tbody>
  </table>
  ${/* Sin un total único no hay valor en letras que escribir. */ ""}
  ${hayEscalas(doc) ? "" : `<div class="son"><strong>SON:</strong> ${montoEnLetras(doc.total)}</div>`}
  <div style="display:grid;grid-template-columns:1.4fr 1fr;gap:16px;margin-top:18px;align-items:start;">
    <div>
      <div class="box">
        <div class="boxtitle">OBSERVACIONES</div>
        <div class="boxbody" style="font-size:11px;min-height:52px;white-space:pre-line;">${htmlParaMostrar(doc.notas || "")}</div>
      </div>
      <div style="font-size:8.5px;color:#4b5563;margin-top:8px;font-style:italic;line-height:1.5;text-align:justify;">
        Material sujeto a disponibilidad de inventario al momento de la orden de compra.
        Imágenes de referencia. Para validar el producto y color, se recomienda solicitar muestra.
        Todos los precios son MAS IVA. Cambios por defectos de fabricación o marca. No por mala
        manipulación ni productos que hayan sido usados, modificados o que se encuentre en estado y
        condiciones diferentes al producto inicialmente suministrado. Envío a un punto en Bogotá.
      </div>
    </div>
    ${totalesDoc(doc, { bruto: true })}
  </div>
  <div style="display:grid;grid-template-columns:1fr 1fr;gap:48px;margin-top:14px;">
    <div class="sig">Firma y sello — ${escapeHTML(EMPRESA.nombre)}</div>
    <div class="sig">Aceptación del cliente (firma y fecha)</div>
  </div>
  ${pieFiscal(info, doc)}
  </div>`;
}

// --------------------------------------------------------------------------
// ORDEN DE COMPRA A PROVEEDOR (OCP) — MTHC-008
// --------------------------------------------------------------------------
function cuerpoOCP(info, doc, proveedor) {
  const itemsHTML = (doc.items || []).map((it, i) => `
    <tr>
      <td style="text-align:center;width:60px;font-weight:700;color:${NAVY};">${i + 1}</td>
      <td><strong>${escapeHTML(it.articulo)}</strong>${detalleItem(it, "OCP")}</td>
      <td style="text-align:right;width:60px;">${it.cantidad || 0}</td>
      <td style="text-align:right;width:110px;">${fmtCOP(it.precio_unitario)}</td>
      <td style="text-align:right;width:110px;font-weight:700;">${fmtCOP(it.total)}</td>
    </tr>`).join("");

  return `
  ${encabezadoDoc(info, doc)}
  <div style="display:grid;grid-template-columns:1.2fr 1fr;gap:16px;">
    <div class="box">
      <div class="boxtitle">PROVEEDOR</div>
      <div class="boxbody">
        ${filaCampo("Proveedor", proveedor.nombre)}
        ${filaCampo("NIT", proveedor.nit)}
        ${filaCampo("Dirección", proveedor.direccion)}
        ${filaCampo("Teléfono", proveedor.telefono)}
        ${filaCampo("Correo", proveedor.email)}
        ${filaCampo("Contacto", proveedor.contacto)}
      </div>
    </div>
    <div class="box">
      <div class="boxtitle">DATOS DE LA ORDEN</div>
      <div class="boxbody">
        ${filaCampo("Fecha", fmtDate(doc.fecha))}
        ${filaCampo("Orden No.", doc.numero)}
        ${filaCampo("Fecha de entrega", doc.fecha_entrega ? fmtDate(doc.fecha_entrega) : "")}
        ${filaCampo("Condiciones de pago", doc.condiciones_pago)}
      </div>
    </div>
  </div>
  <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-top:18px;">
    ${bloqueFacturarA()}
    ${bloqueEnviarA(doc, EMPRESA.direccion)}
  </div>
  <!-- Aviso al proveedor: sin el número de la OCP en su factura, cruzarla es
       un trabajo manual. Va destacado a propósito para que no pase inadvertido. -->
  <div style="margin-top:16px;border:2px solid #c0392b;border-left:9px solid #c0392b;border-radius:6px;background:#fdf3f2;padding:11px 14px;display:flex;align-items:center;gap:11px;">
    <div style="font-size:20px;line-height:1;">📌</div>
    <div>
      <div style="color:#c0392b;font-weight:800;font-size:13px;letter-spacing:.2px;">
        Por favor agregar este número de Orden de Compra a su Factura
      </div>
      <div style="color:#c0392b;font-weight:800;font-size:15px;margin-top:3px;letter-spacing:.5px;">
        ${escapeHTML(doc.numero || "")}
      </div>
    </div>
  </div>
  <table class="items">
    <thead><tr>
      <th style="text-align:center;">ARTÍCULO NO.</th><th>DESCRIPCIÓN</th><th style="text-align:right;">CANT</th>
      <th style="text-align:right;">PRECIO POR UNIDAD</th><th style="text-align:right;">TOTAL</th>
    </tr></thead>
    <tbody>${itemsHTML}</tbody>
  </table>
  <div style="display:grid;grid-template-columns:1.4fr 1fr;gap:16px;margin-top:18px;align-items:start;">
    <div class="box">
      <div class="boxtitle">OBSERVACIONES / INSTRUCCIONES</div>
      <div class="boxbody" style="font-size:11px;min-height:52px;white-space:pre-line;">${htmlParaMostrar(doc.notas || "")}</div>
    </div>
    ${totalesDoc(doc)}
  </div>
  <div style="margin-top:26px;text-align:center;" class="gracias">GRA<span>CIAS</span></div>
  <div style="display:grid;grid-template-columns:1fr 1fr;gap:48px;margin-top:8px;">
    <div class="sig">Firma autorizada</div>
    <div class="sig">Fecha</div>
  </div>
  <div style="margin-top:16px;font-size:10px;color:#6d7896;text-align:center;line-height:1.7;">
    Si tiene preguntas sobre esta orden de compra, póngase en contacto con:<br>
    <strong style="color:${NAVY};">${escapeHTML(EMPRESA.nombre)}</strong> • Tel: ${escapeHTML(EMPRESA.telefono)} • ${escapeHTML(EMPRESA.email)}
  </div>
  ${pieFiscal(info, doc)}`;
}

// --------------------------------------------------------------------------
// ORDEN DE PRODUCCIÓN (OP) — MTHC-009
// --------------------------------------------------------------------------
function cuerpoOP(info, doc, cliente) {
  const ctOrigen = doc._origen && doc._origen.tipo === "CT" ? doc._origen.numero : "";
  const entregas = (doc._entregas || []).filter(e => e.fecha)
    .map(e => fmtDate(e.fecha) + (e.nota ? ` (${e.nota})` : "")).join(" • ");

  const itemsHTML = (doc.items || []).map((it, i) => `
    <tr>
      <td style="text-align:center;width:60px;font-weight:700;color:${NAVY};">${i + 1}</td>
      <td><strong>${escapeHTML(it.articulo)}</strong>${it.descripcion ? `<div style="margin-top:2px;">${escapeHTML(it.descripcion)}</div>` : ""}${detalleItem(it, "OP")}</td>
      <td style="text-align:right;width:60px;">${it.cantidad || 0}</td>
      <td style="text-align:right;width:110px;">${fmtCOP(it.precio_unitario)}</td>
      <td style="text-align:right;width:110px;font-weight:700;">${fmtCOP(it.total)}</td>
    </tr>`).join("");

  return `
  ${encabezadoDoc(info, doc)}
  <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px;">
    <div class="box">
      <div class="boxtitle">FACTURAR A</div>
      <div class="boxbody">
        ${filaCampo("Cliente", cliente.nombre)}
        ${filaCampo("NIT", cliente.nit)}
        ${filaCampo("Dirección", cliente.direccion)}
        ${filaCampo("Teléfono", cliente.telefono)}
        ${filaCampo("E-mail", cliente.email)}
      </div>
    </div>
    ${bloqueEnviarA(doc, cliente.direccion)}
  </div>
  <div class="box" style="margin-top:18px;">
    <div class="boxtitle">CONDICIONES DEL PEDIDO</div>
    <div class="boxbody" style="display:grid;grid-template-columns:1fr 1fr;gap:0 18px;">
      <div>
        ${filaCampo("Cotización aprobada", ctOrigen)}
        ${filaCampo("Vendedor", doc.asesor_comercial)}
        ${filaCampo("OC cliente", doc.numero_oc_cliente)}
      </div>
      <div>
        ${filaCampo("Condiciones de envío", doc.condiciones_envio)}
        ${filaCampo("Forma de pago", doc.condiciones_pago)}
        ${filaCampo("Fecha(s) de entrega", entregas)}
      </div>
    </div>
  </div>
  <table class="items">
    <thead><tr>
      <th style="text-align:center;">ARTÍCULO NO.</th><th>DESCRIPCIÓN</th><th style="text-align:right;">CANT</th>
      <th style="text-align:right;">PRECIO POR UNIDAD</th><th style="text-align:right;">TOTAL</th>
    </tr></thead>
    <tbody>${itemsHTML}</tbody>
  </table>
  <div style="display:grid;grid-template-columns:1.4fr 1fr;gap:16px;margin-top:18px;align-items:start;">
    <div class="box">
      <div class="boxtitle">OBSERVACIONES / INSTRUCCIONES</div>
      <div class="boxbody" style="font-size:11px;min-height:52px;white-space:pre-line;">${htmlParaMostrar(doc.notas || "")}</div>
    </div>
    ${totalesDoc(doc)}
  </div>
  <div style="margin-top:26px;text-align:center;" class="gracias">GRA<span>CIAS</span></div>
  <div style="margin-top:14px;font-size:10px;color:#6d7896;text-align:center;line-height:1.7;">
    Para preguntas relacionadas con este pedido, póngase en contacto con:<br>
    <strong style="color:${NAVY};">${doc.asesor_comercial ? escapeHTML(doc.asesor_comercial) + " • " : ""}${escapeHTML(EMPRESA.nombre)}</strong> • Tel: ${escapeHTML(EMPRESA.telefono)} • ${escapeHTML(EMPRESA.email)}
  </div>
  ${pieFiscal(info, doc)}`;
}

// --------------------------------------------------------------------------
// REMISIÓN (RC) — MTHC-011 (mismo vestido de marca, estructura sencilla)
// --------------------------------------------------------------------------
function cuerpoRC(info, doc, cliente) {
  const itemsHTML = (doc.items || []).map((it, i) => `
    <tr>
      <td style="text-align:center;width:34px;font-weight:700;color:${NAVY};">${i + 1}</td>
      <td><strong>${escapeHTML(it.articulo)}</strong>${detalleItem(it, "RC")}</td>
      <td>${escapeHTML(it.descripcion || "")}</td>
      <td style="text-align:right;width:60px;">${it.cantidad || 0}</td>
      <td style="text-align:center;width:60px;">${it.bolsas || 1}</td>
      <td style="width:150px;">${escapeHTML(it.observaciones || "")}</td>
    </tr>`).join("");

  return `
  ${encabezadoDoc(info, doc)}
  <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px;">
    <div class="box">
      <div class="boxtitle">CLIENTE</div>
      <div class="boxbody">
        ${filaCampo("Cliente", cliente.nombre)}
        ${filaCampo("NIT", cliente.nit)}
        ${filaCampo("Dirección", cliente.direccion)}
        ${filaCampo("Teléfono", cliente.telefono)}
      </div>
    </div>
    <div class="box">
      <div class="boxtitle">DATOS DE LA REMISIÓN</div>
      <div class="boxbody">
        ${filaCampo("Fecha", fmtDate(doc.fecha))}
        ${filaCampo("Remisión No.", doc.numero)}
        ${filaCampo("OP de origen", doc._origen ? doc._origen.numero : "")}
      </div>
    </div>
  </div>
  <table class="items">
    <thead><tr>
      <th style="text-align:center;">#</th><th>ARTÍCULO</th><th>DESCRIPCIÓN</th>
      <th style="text-align:right;">CANT</th><th style="text-align:center;">BOLSAS</th><th>OBSERVACIONES</th>
    </tr></thead>
    <tbody>${itemsHTML}</tbody>
  </table>
  ${doc.notas ? `<div class="box" style="margin-top:18px;"><div class="boxtitle">OBSERVACIONES</div><div class="boxbody" style="white-space:pre-line;">${htmlParaMostrar(doc.notas)}</div></div>` : ""}
  <div style="display:grid;grid-template-columns:1fr 1fr;gap:48px;margin-top:14px;">
    <div class="sig">Entrega — ${escapeHTML(EMPRESA.nombre)}</div>
    <div class="sig">Recibido conforme (firma, fecha y sello)</div>
  </div>
  ${pieFiscal(info, doc)}`;
}

export async function exportPDF(tipo, id) {
  const info = DOC_INFO[tipo];
  const doc = state.data[info.key].find(d => d.id === id);
  if (!doc) return;
  registrarEvento(info.key, doc.id, "imprimir", { referencia: doc.numero });

  // Las fotos de los productos se descargan del bucket y se EMBEBEN antes de
  // armar el HTML: la cotización se abre en otra ventana y se imprime, así que
  // no puede depender de una URL firmada ni de la red en ese instante. Son
  // pocas (las del documento) y quedan en caché para las siguientes.
  const fotos = tipo === "CT"
    ? await embebidasParaDocumento((doc.items || []).map(it => it.articulo))
    : new Map();

  // clienteDeDoc cubre el caso del tercero NO registrado: la cotización lleva
  // encima sus datos, así que el formato impreso sale igual de completo.
  const cliente = clienteDeDoc(doc);
  const proveedor = state.data.proveedores.find(p => p.id === doc.proveedor_id) || {};
  const target = tipo === "OCP" ? proveedor : cliente;

  const filename = nombreArchivoDoc(doc.numero, target.nombre);

  const docConTipo = { ...doc, _tipo: tipo };
  let cuerpo;
  if (tipo === "CT") cuerpo = cuerpoCT(info, docConTipo, cliente, fotos);
  else if (tipo === "OCP") cuerpo = cuerpoOCP(info, docConTipo, proveedor);
  else if (tipo === "OP") cuerpo = cuerpoOP(info, docConTipo, cliente);
  else cuerpo = cuerpoRC(info, docConTipo, cliente);

  abrirVentanaDoc(filename, watermarkBorrador(doc) + marcaLateral() + cuerpo);
}

// ─── Cuenta de cobro ─────────────────────────────────────────────────────────
// Documento con el que una persona natural legaliza y cobra sus servicios
// (transporte, fletes, mano de obra) prestados a la empresa: agrupa varias CxP
// directas, detalla cada una con su OP, e incluye la declaración del Art. 383
// del E.T. La dirección del documento es DE la persona HACIA la empresa.
export function exportCuentaCobroPDF(ccId) {
  const cc = (state.data.cuentas_cobro || []).find(x => x.id === ccId);
  if (!cc) return;
  const p = cc.persona || {};
  const cxps = (state.data.cuentas_pagar || []).filter(x => (cc.cxpIds || []).includes(x.id));
  const total = cxps.reduce((s, x) => s + (Number(x.valor) || 0), 0) || Number(cc.total) || 0;
  // Lo retenido: primero lo que quedó congelado en la cuenta de cobro, y si es
  // de antes de existir el campo, lo que traigan sus CxP.
  const _n = v => Number(v) || 0;
  const retefuente = cc.retefuente != null ? _n(cc.retefuente) : cxps.reduce((s, x) => s + _n(x.retefuente), 0);
  const reteica    = cc.reteica    != null ? _n(cc.reteica)    : cxps.reduce((s, x) => s + _n(x.reteica), 0);
  const retenido   = retefuente + reteica;
  const neto       = cc.neto != null ? _n(cc.neto) : total - retenido;
  const pctDe = (monto) => total > 0 ? ((monto / total) * 100).toFixed(2).replace(/\.?0+$/, "") : "0";

  const filas = cxps.map((x, i) => `
    <tr>
      <td style="text-align:center;">${i + 1}</td>
      <td>${escapeHTML(fmtDate(x.fecha))}</td>
      <td>${escapeHTML(x.concepto || "")}</td>
      <td>${escapeHTML((x._origen && x._origen.numero) || "—")}</td>
      <td style="text-align:right;">${fmtCOP(x.valor)}</td>
    </tr>`).join("");

  const cuerpo = `
    <div style="text-align:center;margin-bottom:22px;">
      <div style="font-size:20px;font-weight:800;letter-spacing:1px;">CUENTA DE COBRO</div>
      <div style="font-size:13px;font-weight:700;color:#4C5FA8;margin-top:4px;">No. ${escapeHTML(cc.numero || "")}</div>
      <div style="font-size:11px;color:#64748b;margin-top:2px;">${escapeHTML(fmtDate(cc.fecha))}</div>
    </div>

    <div class="box" style="margin-bottom:14px;">
      <div class="boxbody" style="font-size:12px;line-height:1.7;text-align:center;">
        <strong style="font-size:13px;">${escapeHTML(EMPRESA.nombre || "")}</strong><br>
        NIT ${escapeHTML(EMPRESA.nit || "")}${EMPRESA.dv ? "-" + escapeHTML(EMPRESA.dv) : ""}<br>
        ${escapeHTML(EMPRESA.direccion || "")}${EMPRESA.telefono ? " · Tel: " + escapeHTML(EMPRESA.telefono) : ""}
        <div style="margin:10px 0 4px;font-weight:800;letter-spacing:.5px;">DEBE A:</div>
        <strong style="font-size:13px;">${escapeHTML(p.nombre || "")}</strong><br>
        C.C. ${escapeHTML(p.cedula || "")}
      </div>
    </div>

    <div class="box" style="margin-bottom:14px;">
      <div class="boxtitle">POR CONCEPTO DE</div>
      <div class="boxbody" style="font-size:11.5px;">${escapeHTML(cc.motivo || "")}</div>
    </div>

    <table style="width:100%;border-collapse:collapse;font-size:11px;margin-bottom:6px;">
      <thead><tr>
        <th style="width:28px;">#</th><th style="width:78px;">Fecha</th><th>Detalle</th>
        <th style="width:80px;">Orden</th><th style="width:110px;text-align:right;">Valor</th>
      </tr></thead>
      <tbody>${filas}</tbody>
      <tfoot>
        <tr>
          <td colspan="4" style="text-align:right;font-weight:800;">${retenido > 0 ? "SUBTOTAL" : "LA SUMA TOTAL DE"}</td>
          <td style="text-align:right;font-weight:800;font-size:13px;">${fmtCOP(total)}</td>
        </tr>
        ${retefuente > 0 ? `<tr>
          <td colspan="4" style="text-align:right;">Menos retención en la fuente (${pctDe(retefuente)}%)</td>
          <td style="text-align:right;">− ${fmtCOP(retefuente)}</td>
        </tr>` : ""}
        ${reteica > 0 ? `<tr>
          <td colspan="4" style="text-align:right;">Menos retención de ICA (${pctDe(reteica)}%)</td>
          <td style="text-align:right;">− ${fmtCOP(reteica)}</td>
        </tr>` : ""}
        ${retenido > 0 ? `<tr>
          <td colspan="4" style="text-align:right;font-weight:800;">NETO A PAGAR</td>
          <td style="text-align:right;font-weight:800;font-size:13px;">${fmtCOP(neto)}</td>
        </tr>` : ""}
      </tfoot>
    </table>
    <div style="font-size:10.5px;color:#334155;margin-bottom:16px;"><strong>SON:</strong> ${escapeHTML(montoEnLetras(neto))}${retenido > 0 ? " (neto, después de retenciones)" : ""}</div>

    ${/* La declaración del art. 383 es un JURAMENTO de la persona: solo se
          imprime si su ficha dice que la firma. Ponerla por defecto se la
          atribuía a todo el mundo —incluido un transportador con camión, a
          quien ese artículo no le aplica— en un documento que va a firmar. */ ""}
    ${(cc.persona || {}).declara_383 ? `
    <div class="box" style="margin-bottom:22px;">
      <div class="boxtitle">DECLARACIÓN</div>
      <div class="boxbody" style="font-size:10.5px;line-height:1.65;">
        Me permito manifestar bajo la gravedad de juramento que durante el presente año no tomaré costos
        ni deducciones asociados a los cobros de mis servicios, y solicito me sea aplicado el
        artículo 383 del Estatuto Tributario.
      </div>
    </div>` : ""}

    <div style="margin-top:46px;">
      <div style="border-top:1.5px solid #0f172a;width:280px;padding-top:6px;font-size:11px;">
        <strong>${escapeHTML(p.nombre || "")}</strong><br>
        C.C. ${escapeHTML(p.cedula || "")}
      </div>
    </div>`;

  abrirVentanaDoc(`Cuenta de cobro ${cc.numero || ""} - ${p.nombre || ""}`.trim(), cuerpo);
}

// `facturaPreview` permite imprimir una factura que TODAVÍA no existe: la vista
// previa de emisión muestra así el documento exacto que recibirá el cliente.
// ─── Las retenciones del cliente y el neto, en el PDF de la factura ─────────
// La pantalla de la factura ya mostraba «(−) Retenciones del cliente» y el neto,
// pero el PDF —lo que se manda y se archiva— solo traía el TOTAL. El cliente
// agente retenedor paga el neto; la factura vale el total. Las dos cifras
// tienen que estar en el papel para que nadie lea el neto como un descuento.
//
// Mismo criterio que la cartera (`getRetencionesFactura` en facturas.js): una
// factura guardada solo tiene retenciones si su venta se contabilizó y no está
// anulada. En la vista previa manda lo marcado en la previa (`f.retenciones`).
export function retencionesFacturaPDF(f, esPreview) {
  const cero = { montoRF: 0, montoRICA: 0, montoRIVA: 0, total: 0 };
  if (!f || f.estado === "anulada") return cero;
  if (!esPreview && !f._asientoVenta) return cero;
  return calcularRetencionesVenta(f);
}

function bloqueRetencionesFactura(f, esPreview) {
  const r = retencionesFacturaPDF(f, esPreview);
  if (!(r.total > 0)) return "";
  const sub = parseNumber(f.subtotal) || 0;
  const iva = parseNumber(f.iva) || 0;
  // El % se deduce del monto (la factura guarda montos, no tarifas): hasta tres
  // decimales, que es como viene el ICA de Bogotá (1,104 %).
  const pct = (monto, base) => base > 0
    ? (Math.round(monto / base * 100000) / 1000).toLocaleString("es-CO", { maximumFractionDigits: 3 }) + " %"
    : "";
  const fila = (lbl, monto, base) => monto > 0
    ? `<div style="font-size:11.5px;color:#92400e;">${lbl} (${pct(monto, base)}): <strong>− ${fmtCOP(monto)}</strong></div>` : "";
  return `
    <div style="margin-top:10px;padding-top:8px;border-top:1px dashed #cbd5e1;">
      <div style="font-size:10px;color:#64748b;margin-bottom:3px;">Retenciones que practica el cliente</div>
      ${fila("Retención en la fuente", r.montoRF, sub)}
      ${fila("ReteICA", r.montoRICA, sub)}
      ${fila("ReteIVA", r.montoRIVA, iva)}
      <div style="font-size:15px;font-weight:800;color:#0E7490;margin-top:4px;">NETO A PAGAR: ${fmtCOP((parseNumber(f.total) || 0) - r.total)}</div>
      <div style="font-size:9px;color:#94a3b8;margin-top:2px;">El valor de la factura es el TOTAL; el cliente consigna el neto y entrega el certificado de las retenciones.</div>
    </div>`;
}

export function exportPDFFactura(factId, facturaPreview) {
  const f = facturaPreview || (state.data.facturas||[]).find(x=>x.id===factId);
  if (!f) { alert("Factura no encontrada"); return; }
  if (!facturaPreview) registrarEvento("facturas", f.id, "imprimir", { referencia: f.numero });
  const cli = state.data.clientes.find(c=>c.id===f.cliente_id)||{};
  const estadoLabel = (ESTADOS.FACT||{})[f.estado] || f.estado;

  const itemsHTML = (f.items||[]).map((it,i) => {
    const bg = i%2===0?"#f9fafb":"white";
    return `<tr style="background:${bg};"><td>${i+1}</td><td><strong>${escapeHTML(it.articulo)}</strong>${composicionKitHTML(it)}</td><td>${escapeHTML(it.descripcion||"")}</td><td style="text-align:right;">${it.cantidad}</td><td style="text-align:right;">${fmtCOP(it.precio_unitario)}</td><td style="text-align:right;font-weight:700;">${fmtCOP(it.total)}</td></tr>`;
  }).join("");

  const nombreFact = nombreArchivoDoc(f.numero, cli.nombre);
  const estilos = `
    *{margin:0;padding:0;box-sizing:border-box}
    body{font-family:Arial;font-size:12px;color:#000;padding:20px;}
    table{border-collapse:collapse;width:100%}
    th,td{padding:6px 8px;border:1px solid #ddd;text-align:left;font-size:11px}
    th{background:#059669;color:white;font-weight:700}
    .header{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:16px;padding-bottom:12px;border-bottom:3px solid #059669;}
    .badge{display:inline-block;background:#059669;color:white;font-size:18px;font-weight:800;padding:6px 14px;border-radius:4px;}
    @page{margin:15mm;size:A4}
    @media print{body{padding:0}.no-print{display:none}}
    /* Mismas reglas para el PDF descargado, que se arma desde la pantalla. */
    html.__pdf body{padding:0}
    html.__pdf .no-print{display:none}
  `;
  const cuerpo = `
  <div class="header">
    <div style="display:flex;gap:14px;align-items:center;">
      <img src="${window.LOGO}" style="height:75px;"/>
      <div>
        <div style="font-size:14px;font-weight:800;color:#059669;">${escapeHTML(EMPRESA.nombre)}</div>
        <div style="font-size:10px;color:#555;">NIT ${escapeHTML(EMPRESA.nit)} · Responsables de IVA</div>
        <div style="font-size:10px;color:#555;">${escapeHTML(EMPRESA.direccion)}</div>
        <div style="font-size:10px;color:#555;">Tel: ${escapeHTML(EMPRESA.telefono)} · ${escapeHTML(EMPRESA.email)}</div>
      </div>
    </div>
    <div style="text-align:right;">
      <div style="font-size:10px;color:#888;">MTHC-012</div>
      <div class="badge">FACTURA ELECTRÓNICA</div>
      <div style="font-size:18px;font-weight:700;color:#059669;margin-top:6px;">${escapeHTML(f.numero)}</div>
      <div style="font-size:11px;color:#555;margin-top:2px;">Fecha: ${fmtDate(f.fecha)}</div>
      ${f.fecha_vencimiento?`<div style="font-size:11px;color:#d97706;margin-top:2px;font-weight:700;">Vence: ${fmtDate(f.fecha_vencimiento)}</div>`:""}
    </div>
  </div>
  <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:14px;">
    <div style="padding:12px;background:#f8fafc;border-radius:8px;">
      <div style="font-size:10px;font-weight:700;color:#059669;margin-bottom:4px;">CLIENTE</div>
      <div style="font-size:14px;font-weight:700;">${escapeHTML(cli.nombre||"")}</div>
      <div style="font-size:10px;color:#555;">NIT ${escapeHTML(cli.nit||"—")}</div>
      ${cli.telefono?`<div style="font-size:10px;color:#555;">Tel: ${escapeHTML(cli.telefono)}</div>`:""}
      ${cli.email?`<div style="font-size:10px;color:#555;">${escapeHTML(cli.email)}</div>`:""}
      ${cli.direccion?`<div style="font-size:10px;color:#555;">${escapeHTML(cli.direccion)}</div>`:""}
    </div>
    <div style="padding:12px;background:#f0fdf4;border-radius:8px;border-left:3px solid #059669;">
      <div style="font-size:10px;font-weight:700;color:#059669;margin-bottom:4px;">ESTADO</div>
      <div style="font-size:14px;font-weight:700;">${estadoLabel}</div>
      ${f._origen?`<div style="font-size:10px;color:#555;margin-top:4px;">Remisión: ${escapeHTML(f._origen.numero)}</div>`:""}
      ${f.condiciones_pago?`<div style="font-size:10px;color:#555;margin-top:2px;">Pago: ${escapeHTML(f.condiciones_pago)}</div>`:""}
      ${f._dataico ? (f._dataico.real
        ? `<div style="font-size:9px;color:#065f46;margin-top:4px;font-weight:700;">🇨🇴 Factura electrónica DIAN · Dataico</div>`
        : `<div style="font-size:9px;color:#92400e;margin-top:4px;">⚠️ CUFE simulado (sin conexión real a Dataico) · ${f._dataico.ambiente==="prod"?"Producción":"Pruebas"}</div>`) : ""}
    </div>
  </div>
  <table><thead><tr><th>#</th><th>Artículo</th><th>Descripción</th><th style="text-align:right;">Cant</th><th style="text-align:right;">P. Unit</th><th style="text-align:right;">Total</th></tr></thead><tbody>${itemsHTML}</tbody></table>
  <div style="text-align:right;margin-top:12px;padding-top:10px;border-top:2px solid #059669;">
    <div style="font-size:12px;">Subtotal: <strong>${fmtCOP(f.subtotal)}</strong></div>
    ${renglonesIVA(f.items, f.iva).map(r => `<div style="font-size:12px;">${escapeHTML(r.lbl)}: <strong>${fmtCOP(r.valor)}</strong></div>`).join("")}
    <div style="font-size:20px;font-weight:800;color:#059669;margin-top:4px;">TOTAL: ${fmtCOP(f.total)}</div>
    ${bloqueRetencionesFactura(f, !!facturaPreview)}
  </div>
  ${f._dataico ? (f._dataico.real
    ? `<div style="margin-top:12px;padding:8px;background:#f8fafc;border:1px solid #e2e8f0;border-radius:4px;font-size:9px;color:#334155;word-break:break-all;"><strong>CUFE:</strong> ${escapeHTML(f._dataico.cufe||"Consultar en Dataico")} · Factura electrónica validada por la DIAN · Proveedor tecnológico: Dataico S.A.S.<br/>La representación gráfica oficial (con código QR) llega al correo del cliente desde Dataico.</div>`
    : `<div style="margin-top:12px;padding:8px;background:#fef3c7;border-radius:4px;font-size:9px;color:#92400e;"><strong>⚠️ CUFE simulado, no válido ante la DIAN:</strong> ${escapeHTML(f._dataico.cufe||"")}</div>`) : ""}
  ${f.notas ? `<div style="margin-top:10px;padding:8px;background:#fffbeb;border-left:3px solid #f59e0b;font-size:11px;"><strong>Notas:</strong> ${htmlParaMostrar(f.notas)}</div>` : ""}
  <div style="margin-top:14px;font-size:9px;color:#999;text-align:center;border-top:1px solid #eee;padding-top:8px;">Responsables de IVA · No somos grandes contribuyentes · No somos autoretenedores · ${escapeHTML(EMPRESA.nombre)} · NIT ${escapeHTML(EMPRESA.nit)}</div>
  `;

  abrirVentanaDoc(nombreFact, cuerpo, { estilos, margenMM: 15 });
}

export function exportPDFNotaCredito(ncId) {
  const nc = (state.data.notas_credito||[]).find(x=>x.id===ncId);
  if (!nc) { alert("Nota crédito no encontrada"); return; }
  const cli = state.data.clientes.find(c=>c.id===nc.cliente_id) || {};

  const itemsHTML = (nc.items||[]).map((it,i) => {
    const bg = i%2===0?"#f9fafb":"white";
    return `<tr style="background:${bg};"><td>${i+1}</td><td><strong>${escapeHTML(it.articulo)}</strong>${composicionKitHTML(it)}</td><td>${escapeHTML(it.descripcion||"")}</td><td style="text-align:right;">${it.cantidad}</td><td style="text-align:right;">${fmtCOP(it.precio_unitario)}</td><td style="text-align:right;font-weight:700;">${fmtCOP(it.total)}</td></tr>`;
  }).join("");

  const nombreNC = nombreArchivoDoc(nc.numero, cli.nombre);
  const estilos = `
    *{margin:0;padding:0;box-sizing:border-box}
    body{font-family:Arial;font-size:12px;color:#000;padding:20px;}
    table{border-collapse:collapse;width:100%}
    th,td{padding:6px 8px;border:1px solid #ddd;text-align:left;font-size:11px}
    th{background:#dc2626;color:white;font-weight:700}
    .header{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:16px;padding-bottom:12px;border-bottom:3px solid #dc2626;}
    .badge{display:inline-block;background:#dc2626;color:white;font-size:18px;font-weight:800;padding:6px 14px;border-radius:4px;}
    @page{margin:15mm;size:A4}
    @media print{body{padding:0}.no-print{display:none}}
    /* Mismas reglas para el PDF descargado, que se arma desde la pantalla. */
    html.__pdf body{padding:0}
    html.__pdf .no-print{display:none}
  `;
  const cuerpo = `
  <div class="header">
    <div style="display:flex;gap:14px;align-items:center;">
      <img src="${window.LOGO}" style="height:75px;"/>
      <div>
        <div style="font-size:14px;font-weight:800;color:#dc2626;">${escapeHTML(EMPRESA.nombre)}</div>
        <div style="font-size:10px;color:#555;">NIT ${escapeHTML(EMPRESA.nit)} · Responsables de IVA</div>
        <div style="font-size:10px;color:#555;">${escapeHTML(EMPRESA.direccion)}</div>
        <div style="font-size:10px;color:#555;">Tel: ${escapeHTML(EMPRESA.telefono)} · ${escapeHTML(EMPRESA.email)}</div>
      </div>
    </div>
    <div style="text-align:right;">
      <div style="font-size:10px;color:#888;">MTHC-013</div>
      <div class="badge">NOTA CRÉDITO</div>
      <div style="font-size:18px;font-weight:700;color:#dc2626;margin-top:6px;">${escapeHTML(nc.numero)}</div>
      <div style="font-size:11px;color:#555;margin-top:2px;">Fecha: ${fmtDate(nc.fecha)}</div>
    </div>
  </div>
  <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:14px;">
    <div style="padding:12px;background:#f8fafc;border-radius:8px;">
      <div style="font-size:10px;font-weight:700;color:#dc2626;margin-bottom:4px;">CLIENTE</div>
      <div style="font-size:14px;font-weight:700;">${escapeHTML(cli.nombre||"")}</div>
      <div style="font-size:10px;color:#555;">NIT ${escapeHTML(cli.nit||"—")}</div>
    </div>
    <div style="padding:12px;background:#fef2f2;border-radius:8px;border-left:3px solid #dc2626;">
      <div style="font-size:10px;font-weight:700;color:#dc2626;margin-bottom:4px;">FACTURA AFECTADA</div>
      <div style="font-size:14px;font-weight:700;">${escapeHTML(nc._origen ? nc._origen.numero : "")}</div>
      <div style="font-size:10px;color:#555;margin-top:2px;"><strong>Motivo:</strong> ${escapeHTML(nc.motivo||"")}</div>
      ${nc._dataico ? (nc._dataico.real
        ? `<div style="font-size:9px;color:#065f46;margin-top:4px;font-weight:700;">🇨🇴 Nota crédito electrónica DIAN · Dataico</div>`
        : `<div style="font-size:9px;color:#92400e;margin-top:4px;">⚠️ CUDE simulado (sin conexión real a Dataico)</div>`) : ""}
    </div>
  </div>
  <table><thead><tr><th>#</th><th>Artículo</th><th>Descripción</th><th style="text-align:right;">Cant</th><th style="text-align:right;">P. Unit</th><th style="text-align:right;">Total</th></tr></thead><tbody>${itemsHTML}</tbody></table>
  <div style="text-align:right;margin-top:12px;padding-top:10px;border-top:2px solid #dc2626;">
    <div style="font-size:12px;">Subtotal: <strong>${fmtCOP(nc.subtotal)}</strong></div>
    ${renglonesIVA(nc.items, nc.iva).map(r => `<div style="font-size:12px;">${escapeHTML(r.lbl)}: <strong>${fmtCOP(r.valor)}</strong></div>`).join("")}
    <div style="font-size:20px;font-weight:800;color:#dc2626;margin-top:4px;">TOTAL NOTA CRÉDITO: ${fmtCOP(nc.total)}</div>
  </div>
  ${nc._dataico ? (nc._dataico.real
    ? `<div style="margin-top:12px;padding:8px;background:#f8fafc;border:1px solid #e2e8f0;border-radius:4px;font-size:9px;color:#334155;word-break:break-all;"><strong>CUDE:</strong> ${escapeHTML(nc._dataico.cude||"Consultar en Dataico")} · Nota crédito electrónica validada por la DIAN · Proveedor tecnológico: Dataico S.A.S.</div>`
    : `<div style="margin-top:12px;padding:8px;background:#fef3c7;border-radius:4px;font-size:9px;color:#92400e;"><strong>⚠️ CUDE simulado, no válido ante la DIAN:</strong> ${escapeHTML(nc._dataico.cude||"")}</div>`) : ""}
  <div style="margin-top:14px;font-size:9px;color:#999;text-align:center;border-top:1px solid #eee;padding-top:8px;">Responsables de IVA · No somos grandes contribuyentes · No somos autoretenedores · ${escapeHTML(EMPRESA.nombre)} · NIT ${escapeHTML(EMPRESA.nit)}</div>
  `;

  abrirVentanaDoc(nombreNC, cuerpo, { estilos, margenMM: 15 });
}

// ============================================================================
// PDFs de nómina y empleados (módulo Informes): desprendible de pago,
// certificado laboral y certificado de ingresos y retenciones.
// Reutilizan los estilos corporativos (estilosDoc/abrirVentanaDoc).
// ============================================================================

function encabezadoRRHH(titulo, subtitulo) {
  return `
  <div class="hdr">
    <div style="display:flex;gap:14px;align-items:center;">
      <img src="${window.LOGO}" style="height:72px;"/>
      <div>
        <div style="font-size:15px;font-weight:800;color:${NAVY};letter-spacing:.4px;">${escapeHTML(EMPRESA.nombre)}</div>
        <div style="font-size:10px;color:#555;">NIT ${escapeHTML(EMPRESA.nit)}</div>
        <div style="font-size:10px;color:#555;">${escapeHTML(EMPRESA.direccion)}</div>
        <div style="font-size:10px;color:#555;">Tel: ${escapeHTML(EMPRESA.telefono)} • ${escapeHTML(EMPRESA.email)}</div>
      </div>
    </div>
    <div style="text-align:right;">
      <div style="background:linear-gradient(90deg,${NAVY} 0%,#3A57A8 55%,${GOLD} 100%);color:white;font-size:14px;font-weight:800;padding:4px 16px;display:inline-block;border-radius:5px;letter-spacing:1px;text-shadow:0 1px 1px rgba(0,0,0,.3);">${titulo}</div>
      ${subtitulo ? `<div style="font-size:12px;font-weight:700;color:${GOLD};margin-top:6px;">${subtitulo}</div>` : ""}
    </div>
  </div>
  <div class="goldline"></div>`;
}

export function exportDesprendibleNomina(liqId, empleadoId) {
  const liq = (state.data.nomina_liquidaciones || []).find(l => l.id === liqId);
  const det = liq && (liq.detalle_empleados || []).find(d => d.empleadoId === empleadoId);
  if (!liq || !det) { alert("No se encontró la liquidación del empleado"); return; }
  const emp = (state.data.empleados || []).find(e => e.id === empleadoId) || {};
  const dev = det.devengado, ded = det.deducciones;

  const fila = (label, valor) => valor ? `<tr><td>${label}</td><td>${fmtCOP(valor)}</td></tr>` : "";
  const otrasDeduccion = (det._ajustes && parseFloat(det._ajustes.otrasDeducciones)) || 0;

  const cuerpo = `
  ${encabezadoRRHH("DESPRENDIBLE DE PAGO", escapeHTML(liq.numero))}
  <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px;">
    <div class="box">
      <div class="boxtitle">EMPLEADO</div>
      <div class="boxbody">
        ${filaCampo("Nombre", det.empleadoNombre)}
        ${filaCampo("Cédula", emp.cedula)}
        ${filaCampo("Cargo", det.empleadoCargo)}
        ${filaCampo("Salario base", fmtCOP(dev.salarioBase))}
        ${filaCampo("Banco", emp.banco ? `${emp.banco}${emp.cuenta_bancaria ? " · " + emp.cuenta_bancaria : ""}` : "")}
      </div>
    </div>
    <div class="box">
      <div class="boxtitle">PERÍODO</div>
      <div class="boxbody">
        ${filaCampo("Liquidación", liq.numero)}
        ${filaCampo("Período", `${fmtDate(liq.periodo_desde)} — ${fmtDate(liq.periodo_hasta)}`)}
        ${filaCampo("Días liquidados", String(dev.diasPeriodo))}
        ${filaCampo("Fecha de pago", liq.fecha_pago ? fmtDate(liq.fecha_pago) : "")}
        ${filaCampo("IBC", fmtCOP(dev.ibc))}
      </div>
    </div>
  </div>
  <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-top:18px;align-items:start;">
    <div>
      <div style="font-size:10px;font-weight:800;color:${NAVY};letter-spacing:1px;margin-bottom:6px;">DEVENGADOS</div>
      <table class="tot">
        ${fila("Salario", dev.salarioProporcional)}
        ${fila("Auxilio de transporte", dev.auxilioTransporte)}
        ${fila("Horas extra / recargos", dev.totalHorasExtra)}
        ${fila("Otros devengados", dev.totalOtros)}
        ${fila("Comisiones", dev.comisiones)}
        <tr class="gran"><td>TOTAL DEVENGADO</td><td>${fmtCOP(dev.totalDevengado)}</td></tr>
      </table>
    </div>
    <div>
      <div style="font-size:10px;font-weight:800;color:${NAVY};letter-spacing:1px;margin-bottom:6px;">DEDUCCIONES</div>
      <table class="tot">
        ${fila("Salud (4%)", ded.saludEmpleado)}
        ${fila("Pensión (4%)", ded.pensionEmpleado)}
        ${fila("Fondo de solidaridad", ded.fondoSolidaridad)}
        ${fila("Otras deducciones", otrasDeduccion)}
        <tr class="gran"><td>TOTAL DEDUCCIONES</td><td>${fmtCOP(ded.totalDeducciones + otrasDeduccion)}</td></tr>
      </table>
    </div>
  </div>
  <div style="margin-top:18px;background:linear-gradient(90deg,${NAVY} 0%,#3A57A8 55%,${GOLD} 100%);border-radius:8px;padding:10px 16px;display:flex;justify-content:space-between;align-items:center;color:#fff;">
    <div style="font-size:12px;font-weight:800;letter-spacing:1px;">NETO A PAGAR</div>
    <div style="font-size:19px;font-weight:800;text-shadow:0 1px 1px rgba(0,0,0,.3);">${fmtCOP(det.netoPagar)}</div>
  </div>
  <div class="son"><strong>SON:</strong> ${montoEnLetras(det.netoPagar)}</div>
  <div style="display:grid;grid-template-columns:1fr 1fr;gap:48px;margin-top:14px;">
    <div class="sig">Empleador — ${escapeHTML(EMPRESA.nombre)}</div>
    <div class="sig">Recibí conforme — ${escapeHTML(det.empleadoNombre)}</div>
  </div>
  <div class="foot">Documento informativo de pago de nómina • ${escapeHTML(EMPRESA.nombre)} • NIT ${escapeHTML(EMPRESA.nit)}</div>`;

  abrirVentanaDoc(`Desprendible_${liq.numero}_${(det.empleadoNombre || "").replace(/[^a-zA-Z0-9]/g, "_")}`, cuerpo);
}

export function exportCertificadoLaboral(empleadoId) {
  const emp = (state.data.empleados || []).find(e => e.id === empleadoId);
  if (!emp) { alert("Empleado no encontrado"); return; }
  const salario = Number(emp.salario_base) || 0;
  const cargoLabel = (CARGO_LABELS[emp.cargo] || emp.cargo || "").toUpperCase();

  const cuerpo = `
  ${encabezadoRRHH("CERTIFICADO LABORAL", "")}
  <div style="max-width:640px;margin:30px auto 0;font-size:13px;line-height:2;color:#1f2937;text-align:justify;">
    <div style="text-align:center;font-weight:800;color:${NAVY};font-size:14px;letter-spacing:1px;margin-bottom:26px;">
      LA SUSCRITA ADMINISTRACIÓN DE ${escapeHTML(EMPRESA.nombre)}<br>NIT ${escapeHTML(EMPRESA.nit)}
    </div>
    <div style="text-align:center;font-weight:800;font-size:14px;margin-bottom:26px;color:${GOLD};letter-spacing:2px;">CERTIFICA:</div>
    <p>Que <strong>${escapeHTML(emp.nombre)}</strong>, identificado(a) con cédula de ciudadanía No. <strong>${escapeHTML(emp.cedula || "—")}</strong>,
    labora en esta empresa desde el <strong>${emp.fecha_ingreso ? fmtDate(emp.fecha_ingreso) : "—"}</strong>,
    desempeñando el cargo de <strong>${escapeHTML(cargoLabel || "—")}</strong>${emp.tipo_contrato ? `, con contrato <strong>${escapeHTML(emp.tipo_contrato)}</strong>` : ""},
    devengando un salario básico mensual de <strong>${fmtCOP(salario)}</strong> (${montoEnLetras(salario)}).</p>
    <p style="margin-top:14px;">La presente certificación se expide a solicitud del interesado a los ${fmtDate(today())}.</p>
  </div>
  <div style="max-width:640px;margin:0 auto;">
    <div class="sig" style="margin-top:70px;max-width:300px;">Firma autorizada — ${escapeHTML(EMPRESA.nombre)}</div>
    <div style="font-size:10px;color:#6d7896;margin-top:4px;">Tel: ${escapeHTML(EMPRESA.telefono)} • ${escapeHTML(EMPRESA.email)}</div>
  </div>
  <div class="foot">${escapeHTML(EMPRESA.nombre)} • NIT ${escapeHTML(EMPRESA.nit)} • ${escapeHTML(EMPRESA.direccion)}</div>`;

  abrirVentanaDoc(`Certificado_Laboral_${(emp.nombre || "").replace(/[^a-zA-Z0-9]/g, "_")}`, cuerpo);
}

export function exportCertIngresosRetenciones(empleadoId, anio) {
  const emp = (state.data.empleados || []).find(e => e.id === empleadoId);
  if (!emp) { alert("Empleado no encontrado"); return; }
  const anioStr = String(anio);
  let devengado = 0, salud = 0, pension = 0, fondoSol = 0, neto = 0, n = 0;
  (state.data.nomina_liquidaciones || [])
    .filter(l => l.estado === "confirmada" && (l.periodo_hasta || "").startsWith(anioStr))
    .forEach(l => {
      (l.detalle_empleados || []).forEach(d => {
        if (d.empleadoId !== empleadoId) return;
        devengado += d.devengado.totalDevengado;
        salud += d.deducciones.saludEmpleado;
        pension += d.deducciones.pensionEmpleado;
        fondoSol += d.deducciones.fondoSolidaridad || 0;
        neto += d.netoPagar;
        n++;
      });
    });

  const cuerpo = `
  ${encabezadoRRHH("INGRESOS Y RETENCIONES", `Año gravable ${anioStr}`)}
  <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px;">
    <div class="box">
      <div class="boxtitle">EMPLEADOR (AGENTE RETENEDOR)</div>
      <div class="boxbody">
        ${filaCampo("Razón social", EMPRESA.nombre)}
        ${filaCampo("NIT", EMPRESA.nit)}
        ${filaCampo("Dirección", EMPRESA.direccion)}
      </div>
    </div>
    <div class="box">
      <div class="boxtitle">EMPLEADO</div>
      <div class="boxbody">
        ${filaCampo("Nombre", emp.nombre)}
        ${filaCampo("Cédula", emp.cedula)}
        ${filaCampo("Cargo", CARGO_LABELS[emp.cargo] || emp.cargo)}
        ${filaCampo("Liquidaciones del año", String(n))}
      </div>
    </div>
  </div>
  <div style="margin-top:18px;max-width:560px;">
    <table class="tot">
      <tr><td>Total pagos laborales devengados</td><td>${fmtCOP(devengado)}</td></tr>
      <tr><td>Aportes obligatorios a salud</td><td>${fmtCOP(salud)}</td></tr>
      <tr><td>Aportes obligatorios a pensión</td><td>${fmtCOP(pension)}</td></tr>
      <tr><td>Fondo de solidaridad pensional</td><td>${fmtCOP(fondoSol)}</td></tr>
      <tr><td>Retención en la fuente practicada</td><td>${fmtCOP(0)}</td></tr>
      <tr class="gran"><td>NETO PAGADO EN EL AÑO</td><td>${fmtCOP(neto)}</td></tr>
    </table>
  </div>
  <div class="son"><strong>SON:</strong> ${montoEnLetras(neto)}</div>
  <div style="margin-top:14px;font-size:10px;color:#6d7896;">Certificado informativo generado desde el sistema con base en las liquidaciones de nómina confirmadas del año ${anioStr}. No reemplaza el Certificado de Ingresos y Retenciones oficial (Formulario 220 DIAN), que debe ser validado por el área contable.</div>
  <div class="sig" style="max-width:300px;">Firma autorizada — ${escapeHTML(EMPRESA.nombre)}</div>
  <div class="foot">${escapeHTML(EMPRESA.nombre)} • NIT ${escapeHTML(EMPRESA.nit)} • Expedido el ${fmtDate(today())}</div>`;

  abrirVentanaDoc(`Cert_Ingresos_${anioStr}_${(emp.nombre || "").replace(/[^a-zA-Z0-9]/g, "_")}`, cuerpo);
}
